import { API } from 'aws-amplify';
import awsmobile from '../aws-exports';

import { getPhonemePlacementEnabled } from '../components/Reader/Speech/Speech';

import io from "socket.io-client";
import {logSessionInfo} from '../services/logger';
import get from 'lodash.get';
import { getUserData, shouldUseNewSpanishPlacementManagerForCurrentSession } from './API';
import { getScreeningWindow } from './screeningWindowService';
import studentRecordService from './StudentRecordService';
import socketLogger from './socketLogger';
import { isMockAOSNow } from './AssessmentOrchestrationService';
import { LocalPlacementAsrHandler } from './LocalPlacementAsrHandler';
// The warmup-derived routing state LIVES in the orchestrator — it is the source
// of truth for server-vs-local ASR, and folding the verdict into its
// shouldUseLocalAsr() is what routes every consumer (phrase routing here, the
// ASR-connection waits, placement_manager labeling, story graph downloads) off one
// answer. asr/localAsrWarmup produces the state (it runs the warmup, knows who cancelled
// an unsettled run, and owns the deadline timer); this module enforces it per phrase.
// Neither stores it. The push direction is what keeps this a one-way dependency: the
// orchestrator imports nothing from here.
// This import rides the API ↔ SpeechStream module cycle that already exists; like
// the rest of that cycle, every cross-reference is call-time, never module-eval.
import {
  LOCAL_ASR_ROUTE,
  getLocalAsrWarmupRouteDecision,
  isLocalAsrProvisionallyServerRouted,
} from './CursorOrchestrator';
import { isLocalAsrEnabled, shouldUseLocalPlacementAsr, resolveLocalAsrPlacementLocale } from './asr/localAsrEnablement';
import {
  recordLocalAsrLifecycle,
  recordLocalAsrFailure,
  flushLocalAsrLifecycle,
  LOCAL_ASR_FALLBACK_REASON,
} from './asr/localAsrLifecycle';
import * as localAsrWarmup from './asr/localAsrWarmup';

let predictionCallback = null;
let comprehensionCallback = null;
let storeTestletItemResponseCallback = null;
let storeTestletItemErrorCallback = null;
let timingCallback = null;
let onDetectionEndCallback = null;
let socket = null;
let transcript = "";    //This is the "base" transcript saved off whenever we disable the placement socket
let currentTranscript = ""; //This is the transcript returned for the current placement session.
let listening = false;
let pauseRequested = false;
let recording = false;
let kaldiTranscript = ""; //This is the Kaldi transcript from EDM
let placementDisconnects = 0;
let edmDisconnects = 0;
let totalBytesSent = 0;
let enabledServices = {
  PlacementManager: false,
  PhonPlacementManager: false,
  SpeechStream: false,
  Nrt: false,
  AmirabetPlacementManager: false,
  AmiraPlacementManager: false,
  GooglePlacementManager: false,
  PhonemeASR: false,
  PartialSpeechStream: false,
  ComprehensionInference: false,
  aos: false
};
let useGoogleNRT = false;
let useSpanishPlacement = false;
let useNewSpanishPlacementManagerMode = false;
let oneOffAudio = false;
let lastPhons = '';
let networkMonitoringService = null;
let audioDestination = null;
let adhocSequence = {};
let lastPhraseIndex = null;
let lastPhraseIndexTranscript = null;
let phraseReplayBuffer = [];        // audioSegment events for the current in-flight phrase
let pendingReplay = null;           // saved on unexpected disconnect; replayed on reconnect
let currentBufferedPhraseIndex = undefined;
let currentBufferedActivityId = undefined;  // activityId phraseReplayBuffer's current phrase belongs to
let pendingReplayActivityId = null;         // activityId pendingReplay's data belongs to
let currentActivityId = undefined;          // authoritative "what activity are we in right now"
let awaitingReconnect = false;              // send-suppression flag, decoupled from pendingReplay's data
let pendingReplayTimeoutHandle = null;      // safety valve: un-stick suppression if reconnect never resolves
let replayAbandoned = false;                // true once the safety valve gives up on THIS disconnect episode
let pendingStop = false;                    // true if stopAll()'s 'stop' emit was suppressed during a disconnect
// reconnect_failed realistically never fires (socket.io v2 default reconnectionAttempts:
// Infinity, no override on the io() call below) -- this timeout is the real backstop.
const PENDING_REPLAY_TIMEOUT_MS = 30000;

// Clears pendingReplay/pendingReplayActivityId, logging when there was
// something real to discard. Shared so every reset site (new activity,
// session teardown, stale-at-reconnect, reconnect_failed) stays in sync --
// the prior cross-activity leak bug (fixed in 1832f60a) happened precisely
// because this reset lived in only one place and a second site was missed.
function clearPendingReplay(reason) {
  if (pendingReplayTimeoutHandle) {
    clearTimeout(pendingReplayTimeoutHandle);
    pendingReplayTimeoutHandle = null;
  }
  if (pendingReplay) {
    logSessionInfo("replay_discarded", {
      reason,
      staleActivityId: pendingReplayActivityId,
      currentActivityId,
      discardedSegments: pendingReplay.length,
    });
  }
  pendingReplay = null;
  pendingReplayActivityId = null;
}

// Safety-valve timer: if a disconnect episode outlasts PENDING_REPLAY_TIMEOUT_MS
// without a 'reconnect', stop suppressing sends and FLUSH whatever's buffered
// so far into socket.io's own native sendBuffer (ahead of any new live audio,
// which will also queue there while still disconnected) instead of discarding
// it -- previously this just threw the data away, so a reconnect after the
// valve fired delivered only the post-timeout tail of an in-flight phrase and
// the server silently scored it truncated (2026-07-16 review).
// Shared by onDisconnect and initSocketCounters: a testlet-to-testlet
// transition mid-outage (ReaderManager's fromRelevel skip / WaitModal's
// unconditional 90s timer, both reachable while still disconnected) needs its
// own backstop too, since clearPendingReplay cancels whichever timer was
// already running for the PREVIOUS activity and nothing else re-arms it.
function armSafetyValve() {
  if (pendingReplayTimeoutHandle) {
    clearTimeout(pendingReplayTimeoutHandle);
  }
  pendingReplayTimeoutHandle = setTimeout(() => {
    pendingReplayTimeoutHandle = null;
    awaitingReconnect = false;
    replayAbandoned = true;
    const bufferedAtTimeout = (pendingReplay || []).concat(phraseReplayBuffer);
    if (bufferedAtTimeout.length > 0) {
      bufferedAtTimeout.forEach(function(event) { emit(socket, 'audioSegment', event); });
      logSessionInfo("replay_flushed_at_timeout", {
        activityId: currentActivityId,
        segments: bufferedAtTimeout.length,
      });
    }
    phraseReplayBuffer = [];
    pendingReplay = null;
    pendingReplayActivityId = null;
    if (pendingStop) {
      pendingStop = false;
      emitStop();
    }
  }, PENDING_REPLAY_TIMEOUT_MS);
}

// --- Local ASR placement state (TT-828) ---
let localAsr = null;
let localAsrInitializing = false;
let localAsrLifecycleId = 0;
/** The in-flight boot, shared by every caller that asks while it runs (TT-1313). App's
 * gated boot awaits it; the legacy fire-and-forget entry keeps returning false meanwhile. */
let localAsrInitPromise = null;
/** Set when an activity hard-failure disposed the engine (CR-10679). While set, the
 * per-activity / mount-time warm calls are recorded no-ops instead of re-booting the WASM
 * engine in the background mid-session; only App's explicit gated boot re-arms it. */
let localAsrRetiredAfterActivityFailure = false;
/** The in-flight teardown, so a boot asked for while a dispose is still settling waits for
 * it instead of reading a handler reference the dispose is about to null (TT-1313). */
let localAsrDisposePromise = null;
/** A prior boot's init promise, parked here by a dispose that bumped its lifecycle while it
 * was still in createModel — which cannot be interrupted, so the doomed worker keeps
 * downloading/constructing for minutes on a slow link (TT-1313). Kept DISTINCT from
 * localAsrInitPromise so no later caller inherits its doomed `false` via the initPromise
 * shortcut (that would hand the next student server ASR for nothing), yet the next gated
 * boot can wait for it to fully settle — and self-dispose its handler — before constructing
 * a fresh engine. Without this park a second boot starts a second WASM engine beside the
 * doomed one: two engines resident at once on a 4 GB device is precisely the CR-10679
 * memory event this PR exists to prevent (PR #6006 review #1). */
let localAsrSettlingInitPromise = null;

const DEBUG_SOCKET = false;

const LOCAL_ASR_PLACEMENT_ROUTE = Object.freeze({
  SERVER: 'server',
  LOCAL: 'local',
});

let localAsrPlacementRoute = LOCAL_ASR_PLACEMENT_ROUTE.SERVER;
let localAsrActivityFailed = false;
// Why localAsrActivityFailed latched, kept in lockstep with it (set where it sets, null
// where it clears) so the flat activity summary cannot report a reason for an activity
// that did not fail. In practice this is LOCAL_ASR_FALLBACK_REASON.INFERENCE_FAILURE or
// null: only inference failure latches at the activity level. RUNTIME_NOT_READY and
// HANDLER_DECLINED are per-phrase reasons that never reach fallbackLocalAsrActivity.
let localAsrActivityFallbackReason = null;

// WIRE VALUES, not just dedupe keys: the three constants below are emitted verbatim in
// local_asr_activity_summary.nonLocalPhraseReasons (a fleet-queried field, TT-1174) alongside
// the LOCAL_ASR_FALLBACK_REASON members. Renaming one changes that field's values, so treat
// them as part of the log contract — a sibling to the shared enum, not internal state.
/**
 * Dedupe key for the by-design ineligibility report, which shares the fallback set so
 * there is one per-activity latch rather than two.
 */
const LOCAL_ASR_INELIGIBLE_KEY = 'phraseIneligible';

/**
 * Dedupe key for the warmup-gate report — same per-activity latch as the two above,
 * so a gated activity gets one timeline marker rather than one per phrase.
 */
const LOCAL_ASR_WARMUP_GATED_KEY = 'phraseWarmupGated';

/**
 * Dedupe key for the PROVISIONAL warmup-gate report (TT-1218), deliberately separate
 * from the settled one above rather than sharing its latch. This ticket's scenario
 * produces both in the same activity — phrase 0 gated provisionally while the overrun
 * run is still settling, phrases 1..N gated by the verdict it then latched — so one
 * shared key would collapse them into a single record and silently drop whichever
 * arrived second. Two keys, two records, and the pair is the evidence that the
 * provisional call was later confirmed.
 */
const LOCAL_ASR_WARMUP_GATED_PROVISIONAL_KEY = 'phraseWarmupGatedProvisional';

// Per-activity placement tally, reported on runtime.activityEnded. `placement_manager`
// reports localAsr from ELIGIBILITY, which stays true for an activity that never decoded
// a single phrase on device — so without these counts a fully-fallen-back activity is
// indistinguishable in the logs from one that ran entirely locally. `ineligible` is
// counted apart from `server` so an activity the eligibility rules were never going to
// serve locally does not read as one that degraded (see recordLocalAsrServerPhrase).
let localAsrPhraseCounts = { local: 0, server: 0, ineligible: 0 };
// Reasons already reported this activity. A wedged runtime hits the same reason on every
// phrase; the timeline wants the first occurrence, not forty copies.
let localAsrReportedFallbacks = new Set();

function setLocalAsrPlacementRoute(route) {
  localAsrPlacementRoute = route;
  if (route === LOCAL_ASR_PLACEMENT_ROUTE.LOCAL) {
    localAsrActivityFailed = false;
    localAsrActivityFallbackReason = null;
  }
}

/**
 * Whether local ASR was part of this session at all, and so whether an activity boundary
 * has anything to say about it. False on a device or session where local ASR is off, where
 * no runtime is ever built and no phrase is ever tallied.
 * @returns {boolean}
 */
function localAsrTouchedActivity() {
  return Boolean(localAsr)
    || localAsrActivityFailed
    || isLocalAsrEnabled()
    || localAsrPhraseCounts.local > 0
    || localAsrPhraseCounts.server > 0
    || localAsrPhraseCounts.ineligible > 0;
}

/**
 * Counts a phrase the server served and reports the first phrase to hit each distinct
 * reason, distinguishing a phrase local ASR was never eligible for from one it lost.
 *
 * Enablement, asset download, and runtime init are all locale- and activity-independent,
 * so a Spanish story on a local-ASR licensed district arrives here with a ready runtime
 * and declines every phrase on the en-us gate in shouldUseLocalPlacementAsr — as does an
 * assessment activity inside a tutor-classified session, via that helper's activity-scoped
 * assessmentMode backstop. Reporting either as a failure-flagged fallback (and counting it
 * toward `server`, leaving activityEnded reading `localPhrases: 0, serverPhrases: N`) would
 * send an investigation after the eligibility rules doing exactly their job. Those phrases
 * get a plain `placement.phraseIneligible` and their own tally instead.
 *
 * Eligibility is classified by calling shouldUseLocalPlacementAsr rather than re-checking
 * the locale here, so this can never disagree with the gate it is explaining.
 *
 * Nothing is recorded or counted for a session local ASR was never part of: with it off
 * no runtime is ever built, so `localAsr` is null and every phrase of every server-only
 * session reaches here. Tallying those would report that session as having had phrases
 * local ASR was "ineligible" for, and would put a phrase tally on an activity it never
 * touched.
 *
 * The gate needs both halves, not just enablement. Enablement can flip false mid-session
 * with a live runtime (sticky assessment reclassification, see
 * setLocalAsrAssessmentSession), and in that window every phrase is declined by the
 * handler on the same gate. Counting only on enablement dropped those, leaving the
 * activity's own boundary event reporting no phrases at all while `ready` was true. A live
 * handler means local ASR was genuinely in play, so it keeps tallying — as ineligible
 * phrases, since shouldUseLocalPlacementAsr reads enablement too.
 * @param {string} reason One of LOCAL_ASR_FALLBACK_REASON's phrase-scoped members
 *   (RUNTIME_NOT_READY / HANDLER_DECLINED / PHONEME_PLACEMENT); INFERENCE_FAILURE is an
 *   activity-level latch, never a per-phrase report.
 * @param {object} [meta] Phrase metadata, for the activity/phrase the report began at.
 * @returns {void}
 */
function recordLocalAsrServerPhrase(reason, meta = {}) {
  if (!isLocalAsrEnabled() && !localAsr) {
    return;
  }

  const languageCode = meta.locale || meta.languageCode;
  const eligible = shouldUseLocalPlacementAsr({ locale: languageCode });
  const key = eligible ? reason : LOCAL_ASR_INELIGIBLE_KEY;
  const sessionLocale = getUserData().locale;
  const detail = { activityId: meta.activityId, phraseIndex: meta.phraseIndex, languageCode, sessionLocale };

  if (eligible) {
    localAsrPhraseCounts.server += 1;
  } else {
    localAsrPhraseCounts.ineligible += 1;
  }

  if (localAsrReportedFallbacks.has(key)) {
    return;
  }
  localAsrReportedFallbacks.add(key);

  if (!eligible) {
    recordLocalAsrLifecycle('placement.phraseIneligible', detail);
    return;
  }
  recordLocalAsrFailure('placement.phraseFellBackToServer', { reason, ...detail });
}

/**
 * Counts a phrase the warmup gate routed to the server, and reports the first such
 * phrase per activity.
 *
 * A lifecycle event rather than a failure: this is the gate doing exactly its job —
 * the device was measured, found unable to keep pace, and routed to the ASR that can.
 * The decisive numbers live on placement.warmupRouteDecided and runtime.warmupEnded;
 * this marker says the decision was ENFORCED for this activity, which neither of
 * those can (they fire once per session, at story load, possibly under a different
 * activityId than the phrases they end up gating).
 *
 * Counted under `server` rather than a fourth tally: runtime.activityEnded's counts
 * answer "which ASR actually served this activity", and the answer is the server.
 * The marker beside them is what distinguishes gated-by-measurement from degraded.
 * That holds for the provisional gate too — the server served the phrase either way.
 *
 * `provisional` marks the TT-1218 path: gated by a deadline overrun while the run was
 * still settling, so there is no verdict to report yet. The flag is added ONLY on that
 * path, leaving every settled row byte-identical to what it was before TT-1218, so a
 * fleet query counting gated phrases keeps working unchanged and one filtering on the
 * flag gets exactly the new population.
 * @param {object} [meta] Phrase metadata, for the activity/phrase the gate fired at.
 * @param {object} [options]
 * @param {boolean} [options.provisional] Whether this is the pre-verdict overrun gate.
 * @returns {void}
 */
function recordLocalAsrWarmupGatedPhrase(meta = {}, { provisional = false } = {}) {
  localAsrPhraseCounts.server += 1;
  const key = provisional
    ? LOCAL_ASR_WARMUP_GATED_PROVISIONAL_KEY
    : LOCAL_ASR_WARMUP_GATED_KEY;
  if (localAsrReportedFallbacks.has(key)) {
    return;
  }
  localAsrReportedFallbacks.add(key);
  recordLocalAsrLifecycle('placement.phraseWarmupGated', {
    activityId: meta.activityId,
    phraseIndex: meta.phraseIndex,
    verdict: getLocalAsrWarmupRouteDecision()?.verdict,
    ...(provisional ? { provisional: true } : {}),
  });
}

/**
 * Latches server placement for the rest of the activity.
 *
 * Reported as its own event rather than left to the cause event (runtime.error) because
 * the consequence is what a reader needs: placement_manager still reports localAsr from
 * eligibility, and every later phrase goes to the server silently.
 *
 * Reported once. Phrase and intervention modes track consecutive inference failures
 * separately, so the second mode can report a failure after the activity has already
 * fallen back — the timeline wants one latch, not one per mode.
 * @param {string} reason
 * @param {object} [detail]
 * @returns {void}
 */
function fallbackLocalAsrActivity(reason, detail = {}) {
  if (localAsrActivityFailed) {
    return;
  }
  recordLocalAsrFailure('placement.activityFellBackToServer', { reason, ...detail });
  localAsrPlacementRoute = LOCAL_ASR_PLACEMENT_ROUTE.SERVER;
  localAsrActivityFailed = true;
  localAsrActivityFallbackReason = reason;
}

/**
 * Whether local ASR is actually driving placement right now — the route is LOCAL,
 * the activity has not fallen back to the server, and the handler is ready.
 * Consumers gating behavior on "the student does not need the network to keep
 * reading" must use this rather than the enablement flag, which is true even when
 * an init or activity failure has already pushed placement back to the server.
 * @returns {boolean}
 */
export function isLocalAsrPlacementRouteActive() {
  return localAsrPlacementRoute === LOCAL_ASR_PLACEMENT_ROUTE.LOCAL
    && !localAsrActivityFailed
    && Boolean(localAsr && localAsr.isReady);
}

/**
 * Whether the local ASR runtime is initialized and has not latched an activity-level
 * failure. Unlike isLocalAsrPlacementRouteActive() this does NOT require the route to
 * be LOCAL, so it is answerable at activity start — before the first startPhrase sets a
 * route — which is what the placement_manager log needs to distinguish "eligible for
 * local ASR and the runtime is up" from "eligible but still falling back to the server".
 * @returns {boolean}
 */
export function isLocalAsrRuntimeReady() {
  return Boolean(localAsr && localAsr.isReady) && !localAsrActivityFailed && !localAsrRetiredAfterActivityFailure;
}

function suppressServerPlacementManager(services) {
  services.PlacementManager = false;
}

export async function getRegionalURL(regionOverride){
  if(!regionOverride){
    const url = process.env.GLOBAL_DISCOVERY_URL;
    return fetch(url,{
      method: "GET",
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then((resp) => resp.json())
    .then((resp)=>{
      console.log("discovery",resp)
      logSessionInfo('region_'+resp.discovery.region,resp)
      return {url: resp.discovery.address, region: resp.discovery.region}
    })
    .catch((err)=>{
      console.log("Error getting region",err)
      logSessionInfo('error_getting_region',{region: process.env.UNIFIED_SOCKET})
      const region = 'us-east-2';
      const url = process.env.UNIFIED_SOCKET;
      return {url,region};
    })
  }else{
    // Uncomment for: This connects to a local Scatter Gather for faster socket event development
    //  if (regionOverride === 'localhost') {
    //   const url = 'http://localhost:7111/';
    //   console.log("Overriding region to:",region,url);
    //   logSessionInfo('region_override',{region: region, url: url});
    //   return {url,region};
    // }
    const region = 'us-east-2';
    const url = process.env.UNIFIED_SOCKET;
    console.log("Overriding region to:",region,url);
    logSessionInfo('region_override',{region: region, url: url});
    return {url,region};
  }

}

function emit(socket, event, payload) {
  if(DEBUG_SOCKET) {
    console.log(`%cEMIT ${event}`, 'background: #ffcc99; color: #000; padding: 1em;', JSON.stringify(Object.assign({}, payload, {'data': null}), null, 0));
  }
  // Log outgoing socket event
  socketLogger.logOutgoing(event, payload, payload?.services);
  socket && socket.emit(event, payload);
}

export const placementProxy = new Proxy({
  connected: null,
  onConnectedChange: null,
},{
  set: function(obj, prop, newValue) {
    if(obj[prop] !== newValue){
      if(prop === 'connected' && obj.onConnectedChange){
        obj.onConnectedChange(newValue);
      }
      obj[prop] = newValue;
    }
    return true;
  }
});
let placementCallback = null;
let phonCallback=null;
let amirabetCallback=null;
let phonASRCallback = null;

export function startEDMSession(inEDMCallback,inTimingCallback,onDetectionEnd,inIsSpanishPilot=false){
  // Do not assign useNewSpanishPlacementManagerMode here — startPlacementSession owns it.
  // ReaderManager calls initializeSpeechSession → startPlacementSession before startEDMSession.
  if (DEBUG_SOCKET && inIsSpanishPilot && !useNewSpanishPlacementManagerMode) {
    console.warn('[SpeechStream] Spanish pilot EDM started without new Spanish PM placement mode — check call order / props', { useNewSpanishPlacementManagerMode, inIsSpanishPilot });
  }
  if(!socket){
    initUniversalSocket();
  }

  enabledServices.Nrt = true;
	
  predictionCallback = inEDMCallback;
  timingCallback = inTimingCallback;
  onDetectionEndCallback = onDetectionEnd;
}

export function startRecordingSession(){
  if(!socket){
    initUniversalSocket();
  }
  enabledServices.SpeechStream = true;
}

export async function initUniversalSocket(){
  //TODO: instead of using the URL received at startup, change on every activity.
  let socketURL = getUserData().speechStreamURL;
  if(!socketURL){
    socketURL = (await getRegionalURL()).url
  }

  logSessionInfo("socket_connecting",{event: 'connecting', timestamp: Date.now(),socketURL})
  socket = io(socketURL, {secure: true, 'connect timeout': 2000, timeout: 2000, transports: ['websocket']});

  var onevent = socket.onevent;
  socket.onevent = function (packet) {
    var args = packet.data || [];
    onevent.call (this, packet);    // original call
    packet.data = ["*"].concat(args);
    onevent.call(this, packet);      // additional call to catch-all
  };

  if(process.env.STREAM){
    socket.on('connect', ()=>{
      placementProxy.connected = true;
      console.log("connected to EDM socket", socket);
      enabledServices.SpeechStream = true;  //Enable saving audio by default
      placementProxy.connected = true;
      logSessionInfo("socket_connected",{event: 'connect',id: socket.id, timestamp: Date.now()});
      socketLogger.logConnection('connect', { socketId: socket.id, socketURL });
    });

    socket.on('decodingError', (data) => {
      //console.log('Decoding error', { data });
    });

    socket.on('debug-steps', (data) => {
      //console.log('Google Recognizer step', { data }); Leave in for debugging
    });

    socket.on('audioSegment', (data) => {
      console.log('Data from Speech Socket', { data });
    });

    socket.on('connect_error', (error) => {
      console.log("Speech Socket Connect Error");
      logSessionInfo("socket_connect_error",{event: 'connect_error', error: error?.message || String(error), timestamp: Date.now()});
      socketLogger.logConnection('connect_error', { error: error?.message || String(error) });
    });

    socket.on('connect_timeout', (timeout) => {
      console.log("Speech Socket Connect Timeout");
      logSessionInfo("socket_connect_timeout",{event: 'connect_timeout', timeout: timeout, timestamp: Date.now()});
      socketLogger.logConnection('connect_timeout', { timeout });
    });

    socket.on('disconnect', function(reason) {
      // else the socket will automatically try to reconnect
      onDisconnect(reason);
      console.log("Speech Socket Disconnect",reason);
      logSessionInfo("socket_disconnect",{event: 'disconnect', reason: reason, timestamp: Date.now()});
      socketLogger.logConnection('disconnect', { reason });
    });

    socket.on('reconnect_error', (error) => {
      console.log("Error reconnecting to socket",error);
      logSessionInfo("socket_reconnect_error",{event: 'reconnect_error', error: error?.message || String(error), timestamp: Date.now()});
      socketLogger.logConnection('reconnect_error', { error: error?.message || String(error) });
    });

    socket.on('reconnect_failed', () => {
      console.log("Reconnecting to socket failed");
      logSessionInfo("socket_reconnect_failure",{event: 'reconnect_failed', timestamp: Date.now()});
      socketLogger.logConnection('reconnect_failed', {});
      // Terminal give-up: un-stick send suppression and drop any stale replay data.
      // Note: this event realistically never fires today (no reconnectionAttempts
      // override on the io() call below -> socket.io v2 default is Infinity); the
      // setTimeout safety valve in onDisconnect is the mechanism that actually
      // protects against a stuck suppression flag in production.
      awaitingReconnect = false;
      replayAbandoned = true;
      phraseReplayBuffer = [];
      pendingStop = false;  // terminal give-up -- no reconnect left to deliver it through
      clearPendingReplay('reconnect_failed');
    });

    socket.on('reconnecting', function() {
      console.log("Speech Socket Reconnecting");
      logSessionInfo("socket_reconnect_failure",{event: 'reconnecting', timestamp: Date.now()});
      socketLogger.logConnection('reconnecting', {});
    });

    socket.on('reconnect', function(attemptNumber) {
      placementProxy.connected = true;
      awaitingReconnect = false;
      // Reconnected successfully -- the safety-valve timeout armed in onDisconnect
      // is no longer needed. Must clear explicitly here too (not only inside
      // clearPendingReplay) so a stale timeout can't later fire during a LATER,
      // unrelated disconnect and incorrectly clear its suppression early.
      if (pendingReplayTimeoutHandle) {
        clearTimeout(pendingReplayTimeoutHandle);
        pendingReplayTimeoutHandle = null;
      }
      // pendingReplay may belong to an activity we've since moved on from (the
      // P0 leak: disconnect -> reconnect fires late, after a DIFFERENT activity
      // has already started) -- mirrors the localAsrLifecycleId compare-before-
      // trust pattern used elsewhere in this file for local-ASR async callbacks.
      if (pendingReplay && pendingReplayActivityId !== currentActivityId) {
        clearPendingReplay('stale_at_reconnect');
      }
      if (replayAbandoned) {
        // The safety-valve timeout already gave up on this episode and resumed
        // live sends via socket.io's own buffer -- those segments will flush
        // automatically now that we're reconnected. Replaying phraseReplayBuffer
        // here too would double-send them. This episode is over; a NEW
        // disconnect starts fresh (onDisconnect resets this flag).
        replayAbandoned = false;
        pendingReplay = null;
        pendingReplayActivityId = null;
        phraseReplayBuffer = [];
        pendingStop = false;  // already flushed by the safety valve's own timeout, if it was set
      } else {
        // Merge pre-disconnect (pendingReplay, if still valid) and during-disconnect
        // (phraseReplayBuffer) segments; skip socket.io sendBuffer (it would send
        // during-disconnect segments out of order relative to this replay).
        const eventsToReplay = (pendingReplay || []).concat(phraseReplayBuffer);
        pendingReplay = null;
        pendingReplayActivityId = null;
        phraseReplayBuffer = [];
        if (eventsToReplay.length > 0) {
          // Route through emit() (not a raw socket.emit) so replayed segments
          // get a socketLogger.logOutgoing record -- otherwise the hardest-to-
          // reproduce case (a real mid-phrase kill) leaves no trace in the log.
          eventsToReplay.forEach(function(event) { emit(socket, 'audioSegment', event); });
          logSessionInfo("replay_executed", { activityId: currentActivityId, segments: eventsToReplay.length });
        }
        // Send any suppressed 'stop' only AFTER the audio it depends on has
        // gone out, or the server could finalize the phrase ahead of its own
        // replayed audio.
        if (pendingStop) {
          pendingStop = false;
          emitStop();
        }
      }
      console.log("Speech Socket Reconnect successful");
      logSessionInfo("socket_reconnected",{event: 'reconnect', attemptNumber: attemptNumber, timestamp: Date.now()});
      socketLogger.logConnection('reconnect', { attemptNumber });
    });

    socket.on('aos.started-store-item-response', function(data, payload) {
      console.log("Speech Socket: aos.started-store-item-response", data, payload);
      if (storeTestletItemResponseCallback) {
        storeTestletItemResponseCallback(data, payload);
      }
    });

    // socket.on('aos.finished-store-item-response', function(data, payload) {
    //   console.log("Speech Socket: aos.finished-store-item-response", data, payload);
    //   if (finishedStoreTestletItemResponseCallback) {
    //     finishedStoreTestletItemResponseCallback(data, payload);
    //   }
    // });

    socket.on('aos.error-store-item-response', function(data, payload) {
      console.log("Speech Socket: aos.error-store-item-response", data, payload);
      if (storeTestletItemErrorCallback) {
        storeTestletItemErrorCallback(data, payload);
      }
    });
    // socket.on('aos.item-pre-scoring', function(data, payload) {
    //   console.log("Speech Socket: aos.item-pre-scoring", data, payload);
    //   if (storeTestletItemResponseCallback) {
    //     storeTestletItemResponseCallback(data, payload);
    //   }
    // });


    socket.on('*', function(data,payload) {
      processSpeechEvent(data,payload);
    });
  }
}

export function endUniversalSocketSession(timeout){
  if(timeout){
    window.setTimeout(endUniversalSocketSession,timeout);
  }else{
    // Clean up unified build retry mechanism when ending socket session
    clearBuildRetryTimeout(buildAudioState);
    buildAudioState.currentRequest = null;
    buildAudioState.sequenceNumbers = [];
    buildAudioState.type = null;

    resetLocalAsrForNextActivity();

    // Trinity client-resilience: this function tears down the socket at the
    // end of EVERY activity (ReaderManager finalizeAssessment/finalizeTutor),
    // not just whole-session logout -- it must reset replay state the same
    // way initSocketCounters does, or a pendingReplay left over from an
    // unresolved disconnect in this activity survives into the next one.
    clearPendingReplay('session_ended');
    phraseReplayBuffer = [];
    currentBufferedPhraseIndex = undefined;
    currentBufferedActivityId = undefined;
    awaitingReconnect = false;
    replayAbandoned = false;
    pendingStop = false;

    socket && socket.disconnect();
    socket = null;
  }
}

function onDetectorsEnd(data){
  onDetectionEndCallback(get(data, 'flagging.detectors', []))
}

function onPrediction(data){
  console.log("Prediction from EDM:",Date.now(),JSON.stringify(data.prediction)); //TODO: Remove once confident in error detection
  if(predictionCallback && !data.error){
    predictionCallback(
      data.prediction,
      data.predictionConfidences, // confidenceLevels,
      data.interventions, // phraseInterventionWeights
      data.intervention_metadata, // Additional metadata for intervention selection
    );
  }else{
    console.log("ERROR from EDM:", data?.error);
    logSessionInfo('error_from_edm', { error: data?.error })
  }
}

function onComprehensionInference(data){
  console.log(`Comprehension Dialog: ${Date.now().toString()} - On Comprehension Inference`, data);
  logSessionInfo('on_comprehension_inference', data);
  if (comprehensionCallback) {
    comprehensionCallback(data);
  }
}

function onComprehensionInferenceError(data){
  console.log(`Comprehension Dialog: ${Date.now().toString()} - On Comprehension Inference Error`, data);
  logSessionInfo('on_comprehension_inference_error', data);
  if (comprehensionCallback) {  
    comprehensionCallback({ error: true, data });
  }
}

/**
 * AmiraPlacementManager (and PlacementManager) contract for "moving on" in the reader:
 * Backend must emit socket event: message = "decoded", payload = { service: "AmiraPlacementManager", result: { ... } }.
 * Transcript string: prefer result.text (canonical); result.transcript is accepted as an alternate field name.
 * Same event name and shape as PlacementManager. Each decoded event updates the UI; when transcript length meets the phrase, the reader advances.
 *
 * Routing: processSpeechEvent calls onDecoded once per decoded event. PlacementManager: explicit branch when !useGoogleNRT && !useSpanishPlacement;
 * AmiraPlacementManager: explicit when useNewSpanishPlacementManagerMode. Catch-all excludes both services.
 */
function onDecoded(data){
  switch(data.service){
    case "PlacementManager":
    case "AmiraPlacementManager": {
      const result = data && data.result;
      // ?? only: null/undefined text falls back to transcript. Empty string is not replaced by transcript, but
      // `if (listening && text)` below still skips updating currentTranscript when text is "" (falsy).
      const text = result && (result.text ?? result.transcript);
      if (DEBUG_SOCKET && result && text == null) {
        console.warn('[SpeechStream] PlacementManager/AmiraPlacementManager decoded but result has no text or transcript', data.service, result);
      }
      if(listening && text) currentTranscript = text;
      let transcriptToSend = oneOffAudio ? currentTranscript : transcript + currentTranscript;

      // Log first placement transcript for each new phrase
      if(lastPhraseIndex !== null && lastPhraseIndex !== undefined && lastPhraseIndex !== lastPhraseIndexTranscript && transcriptToSend){
        logSessionInfo(`first_placement_transcript_phrase_${lastPhraseIndex}`, {
          transcript: transcriptToSend
        });
        lastPhraseIndexTranscript = lastPhraseIndex;
      }

      if(placementCallback){
        placementCallback(transcriptToSend);
        if (networkMonitoringService && data.source !== 'local-asr') {
          networkMonitoringService.handleOnDecoded();
        }
      }
      break;
    }
    case "PhonPlacementManager":
      analyzePhonStream(data.result.text);
      break;
    case "Wav2VecStreamV2":
      analyzeAmirabetStream(data.result.text);
      break;
    case "Wav2VecPhonASR":
      analyzePhonASR(data.result.text)
      break;
  }
}

let lastPhonLength = 0;

function analyzePhonStream(phons){
  if(phons){
    let stream = phons.split("SIL").join("");
    //Replace multiple spaces
    stream = stream.replace(/ +(?= )/g,'');
    stream = stream.split(" ");
    stream = stream.filter((phon)=>phon !== "");
    lastPhonLength = stream.length;
    if(phonCallback) phonCallback(lastPhonLength);
  }

}

function analyzeAmirabetStream(phons){
  //If there have been no updates, just return
  if(phons === lastPhons) return;

  // We only want to issue deltas. We'll compare the strings in two ways:
  // 1. Find the first mismatched character. This will happen when the backend has changed an existing hypothesis
  // 2. If there are no updates, just take the part of the hypothesis that is new.
  let current = phons.split('');
  let previous = lastPhons.split('');
  let firstMismatch = 0;
  let amirabetPhons = '';

  //Deal with hyphotheses that have changed by finding the first mismatch
  for(firstMismatch;firstMismatch < previous.length;firstMismatch++){
    if(current[firstMismatch] !== previous[firstMismatch]){
      amirabetPhons = phons.substring(firstMismatch);
      break;
    }
  }

  //If there were no updated phons, simply grab any updates
  if(amirabetPhons === '' && current.length !== previous.length){
    amirabetPhons = phons.substring(previous.length);
  }

  amirabetPhons = amirabetPhons.trim();
  lastPhons = phons;
  if(amirabetCallback) amirabetCallback(amirabetPhons)
}

function analyzePhonASR(phons){
  let phonASRtranscript = phons.trim();
  if(phonASRCallback) phonASRCallback(phonASRtranscript)
}

function onSaved(data){
  if(!data){
    return;
  }

  // Check if this acknowledgment is for our build request (unified system)
  if(buildAudioState.sequenceNumbers.length > 0 && buildAudioState.sequenceNumbers.includes(data.sequenceNumber)){
    if (!buildAudioState.acknowledged) {
      buildAudioState.acknowledged = true;
      clearBuildRetryTimeout(buildAudioState);
      
      // Update appropriate legacy variable based on build type
      if (buildAudioState.type === BUILD_TYPE_REGULAR) {
        buildAcknowledged = true;
        logSessionInfo("build_ack",{timeStamp: Date.now(), sequenceNumber: data.sequenceNumber, retryCount: buildAudioState.retryCount});
      } else if (buildAudioState.type === BUILD_TYPE_ADHOC) {
        buildAdhocAcknowledged = true;
        logSessionInfo("build_adhoc_ack",{
          timeStamp: Date.now(), 
          sequenceNumber: data.sequenceNumber, 
          audioDestination,
          retryCount: buildAudioState.retryCount
        });
      }
    }
  }

  acknowledgedSequence = data.sequenceNumber;
  if (data.service && data.service === "PartialSpeechStream") {
    adhocAcknowledgedSequence = data.sequenceNumber;
  }
  
  // Only SpeechStream acks correspond to enqueued main-stream ids; a
  // PartialSpeechStream ack carries an adhoc-namespace sequenceNumber and must
  // not dequeue a main-stream entry by numeric collision.
  if (networkMonitoringService && (!data.service || data.service === "SpeechStream")) {
    networkMonitoringService.dequeueAudio(data.sequenceNumber);
  }
}

function onDisconnect(reason){
  placementProxy.connected = false;
  if(reason !== "io client disconnect"){
    placementDisconnects++;
    edmDisconnects++;
    transcript += currentTranscript;
    currentTranscript="";
    // Suppress sends for ANY unexpected disconnect, not just when something
    // happened to be buffered at this exact moment -- otherwise audio sent
    // while still disconnected (but before the next buffered segment) would
    // hit socket.io's own out-of-order sendBuffer, the exact race this
    // feature exists to avoid.
    awaitingReconnect = true;
    replayAbandoned = false;
    pendingStop = false;  // defense-in-depth: every reconnect path already clears
                           // this, so a fresh disconnect episode should never see
                           // it set; making the "fresh episode" invariant explicit
                           // here pre-empts the same class of edge case as above.
    // phraseReplayBuffer's last entry has payload.done === true when the
    // buffered phrase's audio was already fully sent before this drop (the
    // connection died while we were just awaiting the prediction, not
    // mid-stream). Replaying it on reconnect would resend a phrase the
    // server may have already scored -- a duplicate submission, not a
    // recovery. A synthetic client hitting this exact gap in a live
    // drainstop test produced a 112-disconnect cascade against the same
    // backend this app talks to (trinity-loadtest#4). Only hand off to
    // pendingReplay when there's genuinely unsent audio left.
    const bufferedPhraseAlreadySent = phraseReplayBuffer.length > 0
      && phraseReplayBuffer[phraseReplayBuffer.length - 1].payload.done === true;
    if (phraseReplayBuffer.length > 0 && !bufferedPhraseAlreadySent) {
      pendingReplay = phraseReplayBuffer;
      pendingReplayActivityId = currentBufferedActivityId;
    }
    phraseReplayBuffer = [];
    armSafetyValve();
  }
}

function onASROutput(data){
  let usedFallback = false;

  if(data.asr == "kaldi"){
    if(timingCallback){
      timingCallback(data);
    }
    kaldiTranscript = data.data.text;
    usedFallback = (data.data.transcription[0].confidence === 0.850012345);
  }

  if(data.asr == "google"){
    usedFallback = (data.data.transcription[0][0][1] === 0.850012345);
  }

  let output = (data.asr !== "pocketSphinx") ? data.data.text : data.data.pocketsphinx_transcript;

  console.log('%cGot ASR data', 'background: #0c0; color: #fff; padding: 1em;', data.asr,usedFallback ? "FALLBACK":"",":",output);
}

export function getUnconstrainedTranscript(){
  return kaldiTranscript;
}

export function pauseSpeechSession(){
  if(recording && socket){
    pauseRequested = true;
    recording = false;
  }
  if (localAsr) {
    localAsr.pause();
  }
}

export function resumeSpeechSession(){
  if(!recording && socket){
    socket && !recording && emit(socket, 'resume', {
      time: Date.now(),
      version: '2.0.0',
      services: {
        PlacementManager: true,
        PhonPlacementManager: true,
        AmirabetPlacementManager: true,
        AmiraPlacementManager: true,
        GooglePlacementManager: true,
        SpeechStream: true,
        Nrt: true,
        PhonemeASR: true,
        PartialSpeechStream: true,
        ComprehensionInference: true,
        aos: true
      }
    });

    //socket && !recording && send('resume',{services: { PlacementManager: true, SpeechStream: true, Nrt: true }});
    recording = true;
  }
  if (localAsr) {
    localAsr.resume();
  }
}

export function endEDMSession(){
  emit(socket, 'stop', { time: Date.now(), version: '2.0.0', services: { Nrt: true }});
  predictionCallback = null;
  timingCallback = null;
  enabledServices.Nrt = false;
}

export function getAudioDuration(){
  //return the time in mS. This is a simplification of:
  // Time In Milliseconds = 1000 * (total bytes / sample size in bytes) / (sample rate)
  // which = 1000 * (bytes / 2 ) / 16000
  return Math.round(totalBytesSent/32);
}

// Called by Speech.startListening; keep positional args in sync if adding params.
// `spanishContent`: the content is Spanish even if the session locale is not (GT-413), so the
// Spanish PM choice must not fall back to the locale gate.
export function startPlacementSession(callback,phCallback=null,enableAllServices=true,inGoogleNRT=false,abCallback=null,useAmirabetPlacement=false,useSpanish=false,usePhonemeASR=false,useComprehensionASR=false,paCallback=null,inAudioDestination=null,inComprehensionCallback=null,useKaldiPhonPlacement=false, useAOS=false, spanishContent=false){
  useSpanishPlacement = useSpanish;
  useNewSpanishPlacementManagerMode = shouldUseNewSpanishPlacementManagerForCurrentSession(getUserData().assessmentMode, { spanishContent });
  if(!socket){
    initUniversalSocket();
  }else{
    // Nrt is deliberately absent (see endPlacementSession for the full case).
    // SG's NrtHandler.resume() clears the ASR keepalive timers sendPause()
    // started, and this fires on every startListening() -- including
    // intervention transcript requests, which send no NRT audio at all.
    // resumeSpeechSession() issues the NRT resume when audio actually flows.
    socket && emit(socket, "resume",{
      version: '2.0.0',
      services: {
        PlacementManager: true,
        PhonPlacementManager: true,
        SpeechStream: true,
        AmirabetPlacementManager: true,
        AmiraPlacementManager: true,
        GooglePlacementManager: true,
        PhonemeASR: true,
        PartialSpeechStream: true,
        ComprehensionInference: true,
        aos: true
      }});
  }
  placementCallback = callback;
  phonCallback = phCallback;
  amirabetCallback = abCallback;
  phonASRCallback = paCallback;
  comprehensionCallback = inComprehensionCallback;
  listening = true;
  useGoogleNRT = inGoogleNRT;
  enabledServices.PlacementManager = enableAllServices && !useGoogleNRT && !useSpanishPlacement && !usePhonemeASR;
  enabledServices.GooglePlacementManager = useSpanishPlacement && !useNewSpanishPlacementManagerMode;
  enabledServices.AmiraPlacementManager = useSpanishPlacement && useNewSpanishPlacementManagerMode;
  enabledServices.PhonPlacementManager = useKaldiPhonPlacement;
  enabledServices.AmirabetPlacementManager = useAmirabetPlacement;
  enabledServices.PartialSpeechStream = false;
  enabledServices.PhonemeASR = usePhonemeASR;
  enabledServices.SpeechStream = true;
  enabledServices.ComprehensionInference = useComprehensionASR;
  // lowercase is on purpose
  enabledServices.aos = useAOS;
  audioDestination = inAudioDestination;

  if (audioDestination) {
    if (!adhocSequence[audioDestination]) {
      adhocSequence[audioDestination] = 0;
    }
    enabledServices.PartialSpeechStream = true;
  }
}

/**
 * Ends the placement (reader cursor) session for the current listening pass.
 *
 * Nrt is deliberately omitted from the stop payload -- do not "harmonize" it
 * with sendPause/stopAll/resumeSpeechSession. This runs on every
 * stopListening(), which includes mid-phrase stops when an intervention
 * disables the mic. SG's NrtHandler.stop() discards the phrase's accumulated
 * audio cache and shuts down its ASR streams, so an Nrt-bearing stop here left
 * EDM scoring the phrase on only the audio that arrived after the intervention;
 * every word read before it fell through to the optimistic fallback and was
 * marked correct (TT-937, introduced as a consistency tweak by TT-476).
 *
 * NRT is torn down by the done:true segment's stopAll() and by endEDMSession().
 * A mid-phrase gap is signaled by sendPause() instead, which keeps the ASR
 * streams and the phrase audio buffer alive across the intervention.
 */
export function endPlacementSession(){
  const activityUsesLocalAsr = isLocalAsrPlacementRouteActive();
  //Close the placement session;
  socket && emit(socket,"stop", {
    version: '2.0.0',
    services: {
      PlacementManager: true,
      PhonPlacementManager: true,
      SpeechStream: true,
      AmirabetPlacementManager: true,
      AmiraPlacementManager: true,
      GooglePlacementManager: true,
      PhonemeASR: true,
      PartialSpeechStream: true,
      ComprehensionInference: true,
      aos: true
    },
  });
  listening = false;
  // Server Kaldi transcripts reset each listening session, so folding completed
  // session text is correct. Local ASR emits phrase-cumulative text across
  // intra-phrase pauses, so folding while it owns the phrase would duplicate words.
  if(!oneOffAudio && !activityUsesLocalAsr){
    transcript += currentTranscript;
  }

  currentTranscript="";
}

/**
 * Clears the accumulated placement transcript.
 *
 * endLocalAsrPhrase distinguishes a phrase-boundary reset (ReaderManager's own
 * phrase start/end, which must end the local ASR phrase so the recognizer is
 * flushed and the next biased graph warmed) from a mid-phrase wipe issued by an
 * intervention's listen, which must leave the phrase armed — the server path keeps
 * its session running across the same wipe, and local ASR has no way to re-arm
 * itself before the next phrase boundary.
 * @param {object} [options]
 * @param {boolean} [options.endLocalAsrPhrase=true]
 * @returns {void}
 */
export function resetPlacementTranscript({ endLocalAsrPhrase = true } = {}){
  transcript = "";
  currentTranscript = "";
  kaldiTranscript = "";
  logSessionInfo("socket_disconnects",{placement: placementDisconnects, edm: edmDisconnects});

  if (localAsr) {
    if (endLocalAsrPhrase) {
      localAsr.endPhrase();
    } else {
      localAsr.resetPhraseTranscript();
    }
  }
}

/**
 * Ends the local ASR intervention lifecycle.
 *
 * Exists because the handler had NO signal for "this intervention is over" and had to
 * infer it from the metadata on incoming audio — which cannot work: intervention audio
 * carries the phrase's recording props, so two interventions in one phrase are
 * indistinguishable by phraseIndex, and inferring from the judged word fails whenever
 * two interventions target the same word (a word repeated in the phrase, or Listen's
 * silence retry). Under streaming decode that left the second intervention appending
 * to the first one's still-open utterance.
 *
 * Called from Speech.cancelTranscriptRequest, which every intervention end and abort
 * routes through (Listen.handleMatches on resolve, Listen.abortListen on abort), so the
 * handler is told rather than guessing. Safe to call when no intervention is active.
 * @returns {void}
 */
export function endLocalAsrIntervention(){
  if (localAsr) {
    localAsr.endIntervention();
  }
}

function sendPause(){
  //###console.log("PAUSE")
  socket && emit(socket, 'pause',{
    time: Date.now(),
    version: '2.0.0',
    services: {
      PlacementManager: true,
      PhonPlacementManager: true,
      AmirabetPlacementManager: true,
      AmiraPlacementManager: true,
      GooglePlacementManager: true,
      SpeechStream: true,
      Nrt: true,
      PhonemeASR: true,
      PartialSpeechStream: true,
      ComprehensionInference: true,
      aos: true
    }
  });
}

export function getDisconnects(){
  return {placementDisconnects: placementDisconnects, edmDisconnects: edmDisconnects};
}

export function initSocketCounters(activityId){
  placementDisconnects = 0;
  edmDisconnects = 0;
  sequenceNumber = 0;
  buildSequence = 0;
  buildAdhocSequence = 0;
  totalBytesSent = 0;
  acknowledgedSequence = 0;
  buildAcknowledged = false;
  buildAdhocAcknowledged = false;
  adhocAcknowledgedSequence = 0;
  adhocSequence = {};
  lastPhraseIndex = null;
  lastPhraseIndexTranscript = null;
  // Reset phrase replay buffer state (trinity/client-resilience): without this,
  // a pendingReplay left over from a disconnect in the previous activity survives
  // into the next activity and gets replayed onto its socket on the next reconnect.
  // currentActivityId is the authoritative "what activity are we in" signal used
  // by the reconnect handler's stale-replay check; awaitingReconnect must be
  // reset here too -- otherwise a disconnect in the previous activity that never
  // resolved (no reconnect, no endUniversalSocketSession) leaves every send in
  // this new activity silently suppressed until the old socket's reconnect (or
  // the safety-valve timeout) eventually fires.
  currentActivityId = activityId;
  clearPendingReplay('new_activity_init');
  phraseReplayBuffer = [];
  currentBufferedPhraseIndex = undefined;
  currentBufferedActivityId = undefined;
  // A stop suppressed by the PREVIOUS activity belongs to that activity, not
  // this one -- always discard rather than let it fire into the new session.
  pendingStop = false;
  // Only clear suppression/abandon state if the socket is actually connected.
  // If a disconnect episode from the PREVIOUS activity is still open (no
  // 'reconnect'/'reconnect_failed'/safety-valve resolution yet -- e.g. a
  // testlet-to-testlet transition mid-outage via ReaderManager's fromRelevel
  // skip or WaitModal's unconditional 90s timer, both reachable while still
  // disconnected), resetting these unconditionally let the new activity's
  // audio go out live via socket.io's own sendBuffer AND get buffered for
  // replay, so a later reconnect double-sent it. Gating only awaitingReconnect
  // (not replayAbandoned too) is insufficient: if the safety valve already
  // fired before this transition, awaitingReconnect is already false and a
  // guard on it alone is a no-op, while replayAbandoned's unconditional reset
  // still re-arms buffering while disconnected -- same double-send, different
  // trigger (confirmed reachable via critic-challenger review, 2026-07-16).
  const socketStillDisconnected = socket && !socket.connected;
  awaitingReconnect = socketStillDisconnected ? awaitingReconnect : false;
  replayAbandoned = socketStillDisconnected ? replayAbandoned : false;
  if (socketStillDisconnected && awaitingReconnect && !replayAbandoned) {
    // clearPendingReplay just cancelled the previous episode's safety-valve
    // timer along with its stale pendingReplay -- re-arm it so the NEW
    // activity's phraseReplayBuffer still gets a backstop if this outage
    // outlasts PENDING_REPLAY_TIMEOUT_MS.
    armSafetyValve();
  }

  // Reset unified build retry mechanism (single state for both build types)
  clearBuildRetryTimeout(buildAudioState);
  buildAudioState.retryCount = 0;
  buildAudioState.sequenceNumbers = [];
  buildAudioState.currentRequest = null;
  buildAudioState.acknowledged = false;
  buildAudioState.type = null;
}

let sequenceNumber = 0;
let acknowledgedSequence = 0;
let buildSequence = 0;
let buildAcknowledged = false;
let buildAdhocSequence = 0;
let buildAdhocAcknowledged = false;
let adhocAcknowledgedSequence = 0;

// Unified build retry mechanism constants
const BUILD_RETRY_TIMEOUT_MS = 20000; // 20 seconds
const MAX_BUILD_RETRIES = 2;
const BUILD_TYPE_REGULAR = 'regular';
const BUILD_TYPE_ADHOC = 'adhoc';

// Single unified retry state for both regular and adhoc builds
// Only one build can be in progress at a time
const buildAudioState = {
  timeout: null,
  retryCount: 0,
  sequenceNumbers: [],
  currentRequest: null,
  acknowledged: false,
  type: null  // Will be set to 'regular' or 'adhoc' when build starts
};

function send(type, message) {
  const payload = Object.assign(message,{version: '2.0.1' });
  //console.log(type,JSON.stringify(message.services));
  socket && emit(socket, type, payload);
}

function emitStop() {
  if (!socket) {
    return;
  }
  emit(socket, 'stop', {
    time: Date.now(),
    version: '2.0.0',
    services: {
      PlacementManager: true,
      PhonPlacementManager: true,
      AmirabetPlacementManager: true,
      AmiraPlacementManager: true,
      GooglePlacementManager: true,
      SpeechStream: true,
      Nrt: true,
      PhonemeASR: true,
      PartialSpeechStream: true,
      ComprehensionInference: true,
      aos: true
    }
  });
}

function stopAll(){
  // Suppress 'stop' the same way audioSegment sends are suppressed while
  // disconnected -- otherwise it queues into socket.io's own sendBuffer and
  // flushes on reconnect BEFORE our own Nrt replay (which only runs inside the
  // app's 'reconnect' handler), finalizing the phrase server-side ahead of its
  // own audio (Cursor Bugbot, post-merge review of 2e13fbb03, 2026-07-17).
  if (awaitingReconnect) {
    pendingStop = true;
  } else {
    emitStop();
  }

  enabledServices.PlacementManager = false;
  enabledServices.PhonPlacementManager = false;
  enabledServices.GooglePlacementManager = false;
  enabledServices.AmiraPlacementManager = false;
  enabledServices.AmirabetPlacementManager = false;
  enabledServices.SpeechStream = false;
  enabledServices.GoogleComprehensionASR = false;
  enabledServices.PartialSpeechStream = false;
  enabledServices.ComprehensionInference = false;
  enabledServices.aos = false;
}

export function saveComprehensionTextInput(payload, inComprehensionCallback) {
  comprehensionCallback = inComprehensionCallback;

  const services = {
    ComprehensionInference: true
  };

  let sendToSocket = {
    payload,
    services
  };
  send('processComprehensionQuestion',sendToSocket);
}

export function saveTestletItemResponse(activityId, studentId, gradeLevel, testletId, itemId, itemType, expectedText, actualText, callback, locale = null) {
  if (isMockAOSNow()) {
    callback && callback();
    return;
  }

  enabledServices.aos = true;

  // Normalize locale format from 'en-us'/'es-mx' to 'EN_US'/'ES_MX' format
  let normalizedLocale = null;
  if (locale) {
    normalizedLocale = locale.replace('-', '_').toUpperCase();
  }

  let sendToSocket = {
    payload: {
      studentId: studentId,
      gradeLevel: gradeLevel,
      activityId: activityId,
      testletId: testletId,
      itemId: itemId,
      // locale: normalizedLocale, // TODO: Add this back if and when we have any issues with locale in Spanish calibration assessments
      response: {
        itemType: itemType,
        expectedText: expectedText,
        actualText: actualText,
      },
    },
    services: enabledServices,
  }

  const shouldStartEndSocket = !socket;
  if (shouldStartEndSocket) {
    initUniversalSocket();
  }

  storeTestletItemResponseCallback = (...args) => {
    storeTestletItemResponseCallback = null;

    if (shouldStartEndSocket) {
      endUniversalSocketSession();
    }
    enabledServices.aos = false;
    callback && callback(...args);
  }

  send('storeItemResponse', sendToSocket);
}

export function sendRelevelStoryCompleteToAos(recordingMeta, activityId, studentId, gradeLevel, testletId, itemId, type, shouldUpLevel, shouldDownLevel, callback) {
  if (isMockAOSNow()) {
    callback && callback();
    return;
  }

  let sendToSocket = {
    payload: { 
      ...recordingMeta,
      activityId, 
      studentId, 
      gradeLevel, 
      testletId, 
      itemId,
      activityType: type,
      upLevel: shouldUpLevel,
      downLevel: shouldDownLevel,
      done: true,
    },
    services: {
      aos: true
    },
  }

  const shouldStartEndSocket = !socket;
  if (shouldStartEndSocket) {
    initUniversalSocket();
  }

  storeTestletItemResponseCallback = (...args) => {
    storeTestletItemResponseCallback = null;

    if (shouldStartEndSocket) {
      endUniversalSocketSession();
    }
    enabledServices.aos = false;
    callback && callback(...args);
  }

  // Timeout to ensure that the callback is called if aos.started-store-item-response takes longer than 2 seconds
  setTimeout(() => {
    if (storeTestletItemResponseCallback) {
      storeTestletItemResponseCallback();
    }
  }, 2000);

  // AOS expects an audioSegment event, not a special name
  send('audioSegment', sendToSocket);
}

// metaObj: populated with app session data of choice
export function saveSessionAudio (testID, audioStream, metaObj, allZeros, onFailure, storyid, phraseIndex, samples16k) {
  const enabledServicesCopy = JSON.parse(JSON.stringify(enabledServices));
  let socketData = audioStream;
  metaObj.timeRead = 0 //Stub this in so inference doesn't crash on tutor

  if (metaObj){
    if (!audioDestination) {
      metaObj.sequenceNumber = JSON.parse(JSON.stringify(sequenceNumber));
    } else {
      metaObj.sequenceNumber = JSON.parse(JSON.stringify(adhocSequence[audioDestination]));
    }
  }

  if(useSpanishPlacement){
    metaObj.languageCode = 'es-MX';
  }

  // --- Local ASR activity routing (TT-828) ---
  const activityUsesLocalAsr = isLocalAsrPlacementRouteActive()
    && enabledServicesCopy.PlacementManager === true;

  const sequenceToUse = audioDestination ? adhocSequence[audioDestination] : sequenceNumber;
  let services = metaObj.disableBackend ? {
    PlacementManager: enabledServicesCopy.PlacementManager,
    PhonPlacementManager: enabledServicesCopy.PhonPlacementManager,
    AmirabetPlacementManager: enabledServicesCopy.AmirabetPlacementManager,
    AmiraPlacementManager: enabledServicesCopy.AmiraPlacementManager,
    GooglePlacementManager: enabledServicesCopy.GooglePlacementManager,
    PhonemeASR: enabledServicesCopy.PhonemeASR,
    SpeechStream: false,
    Nrt: false, // discard-audio path: never forward to NRT
    PartialSpeechStream: enabledServicesCopy.PartialSpeechStream,
    ComprehensionInference: enabledServicesCopy.ComprehensionInference,
    aos: enabledServicesCopy.aos
  } : enabledServicesCopy;

  // Local ASR only replaces the server PlacementManager path. Other services
  // remain governed by their normal session flags.
  if (activityUsesLocalAsr) {
    suppressServerPlacementManager(services);
  }

  // Per-segment local inference: skip silent inference, but preserve done:true
  // flushes so a final silent segment can still finalize local ASR consensus.
  // Gated on interventionAudio rather than disableBackend so a phrase re-read
  // still decodes as a phrase: ReaderManager.tryAgain withholds audio from the
  // backend but the student reads the whole sentence, which intervention mode
  // cannot represent because it reduces every emit to the last token. Reached via
  // tryAgain's three default-arg callers: the `read` end-of-phrase intervention,
  // the modal-abort retry, and the Next-button EDM all-true retry.
  if (activityUsesLocalAsr && !metaObj.interventionAudio && (!allZeros || metaObj.done)) {
    const localMeta = {
      activityId: testID,
      phraseIndex,
      done: metaObj.done || false,
    };

    if (samples16k && samples16k.length > 0 && !allZeros) {
      localAsr.audioSamples({ samples16k, ...localMeta });
    } else if (allZeros && metaObj.done) {
      localAsr.audioSamples({ samples16k: new Float32Array(0), ...localMeta });
    } else if (audioStream) {
      localAsr.audioSegment({ blob: audioStream, ...localMeta });
    }
  }

  // One-off intervention transcript request: route through isolated local
  // intervention mode, whose single-word contract matches what the student says.
  if (activityUsesLocalAsr && metaObj.interventionAudio && (!allZeros || metaObj.done)) {
    // The PHRASE text/word list, which is what constrains the recognizer. Named apart
    // from the payload's `interventionTarget` (the judged word) so the two are not read
    // as the same thing.
    const interventionPhraseConstraint = metaObj.expectedText || metaObj.wordList;
    if (interventionPhraseConstraint) {
      const interventionPayload = {
        mode: 'intervention',
        activityId: testID,
        phraseIndex,
        // Identifies the phrase for the biased-HCLG descriptor, so an intervention
        // decodes against the current phrase's graph (on its own intervention-scoped
        // recognizer under streaming decode; the phrase's own cached recognizer in
        // window mode) rather than falling back to the word-list grammar.
        storyId: metaObj.storyId || storyid,
        expectedText: metaObj.expectedText,
        wordList: metaObj.wordList,
        // The word THIS listen is judging (Listen.listener.targetWord, threaded via
        // Speech.requestTranscript). expectedText/wordList above are the PHRASE — that
        // is deliberate and constrains the recognizer (domain fact #2) — so without
        // this the handler cannot tell the judged word from its neighbours and its
        // single emitted token is whatever the decode happened to end on.
        interventionTarget: metaObj.interventionTarget,
        done: metaObj.done || false,
        placementUsesLocalAsr: true,
      };

      if (samples16k && samples16k.length > 0 && !allZeros) {
        localAsr.audioSamples({ samples16k, ...interventionPayload });
      } else if (allZeros && metaObj.done) {
        localAsr.audioSamples({ samples16k: new Float32Array(0), ...interventionPayload });
      } else if (audioStream) {
        localAsr.audioSegment({ blob: audioStream, ...interventionPayload });
      }
    }
  }
  
  let payload = {
    data: socketData, // Audio buffer
    created: Date.now(),
    intermediate: 50, // value in ms that specifies the interval of partial returns
    phonIntermediate: 750,
    phraseId: metaObj.phraseid,
    sequenceNumber: sequenceToUse,
    done: metaObj.done ? metaObj.done : false,
    audioDestination,
    ...metaObj,
  };

  if(metaObj.flagPhrase && metaObj.done){
    payload.flagging = {flag: true}
    logSessionInfo("flagging_phrase",metaObj)
  }

  oneOffAudio = metaObj.disableBackend;

  if(metaObj.comprehensionDone || metaObj.comprehensionDiscard){
    services.ComprehensionInference = true;
    payload.storyid = storyid;
    payload.phraseIndex = phraseIndex;
    metaObj.storyid = storyid;
    metaObj.phraseIndex = phraseIndex;
    metaObj.activityId = testID;

    if (metaObj.comprehensionDiscard) {
      payload.discard = true;
      metaObj.discard = true;
    }
    if (metaObj.comprehensionDone) {
      payload.done = true;
      metaObj.done = true;
    }
  }

  let sendToSocket = {
    payload: payload,
    services: services
  };

  if(services.PlacementManager || services.PhonPlacementManager || services.SpeechStream || services.Nrt || services.GooglePlacementManager || services.AmiraPlacementManager || services.PartialSpeechStream || services.ComprehensionInference || services.aos || services.PhonemeASR){
    // Allow sending when done:true even if allZeros - we must signal the backend that recording is complete
    if((process.env.STREAM) && socket && (!allZeros || payload.done)){
      if(!pauseRequested || metaObj.done || metaObj.discard){
        //NOTE: Do not remove! This is a stopgap solution to trigger recovery from the infamous Blob Error
        let fr = new FileReader();
        fr.readAsDataURL(audioStream);
        fr.onerror = (e) => {
          console.error('FileReader failed: ', e);
          if(onFailure){
            console.log("restarting listening");
            endUniversalSocketSession();
            onFailure();
          }
        };
        // Enqueue only segments guaranteed a unique main-stream sequence id and a
        // matching SpeechStream "saved" ack — the same predicate that increments
        // sequenceNumber below. Kickstart probes are ASR-handshake traffic (sent
        // every 200ms before the backend acks anything) and must not count as a
        // network-health signal; adhoc (audioDestination) segments use a separate
        // id namespace acked by PartialSpeechStream.
        if (services.SpeechStream && !audioDestination && !payload.isKickstartSegment) {
          if (networkMonitoringService) {
            networkMonitoringService.enqueueAudio(payload.sequenceNumber);
          }
        }
        // While awaiting reconnect, suppress send so socket.io doesn't buffer
        // segments that would arrive out-of-order before the replay on reconnect.
        //
        // KNOWN TRADE-OFF (accepted, not fixed): this suppresses the whole
        // combined { payload, services } message, but only services.Nrt gets
        // buffered/replayed below. Any OTHER enabled service in this message
        // (e.g. SpeechStream, which forwards to SQS) is suppressed for the
        // entire disconnect window and never replayed -- that data is dropped,
        // not just delayed. Before this fix, suppression only activated when
        // the Nrt buffer happened to be non-empty at disconnect time, so most
        // SpeechStream sends still flowed through socket.io's own native
        // buffer (delayed, not lost) during a disconnect. Widening suppression
        // to cover every unexpected disconnect (needed to close the P0 replay
        // leak) made this gap unconditional instead of occasional.
        // SpeechStream was already excluded from replay by original design
        // (avoids double-writing to SQS on the new task) -- extending replay
        // to cover it too would need confirming the SQS consumer tolerates a
        // duplicate delivery, which lives in a different service and wasn't
        // checked. Not fixing now; revisit if SpeechStream/SQS gaps during a
        // disconnect turn out to matter downstream.
        if (!awaitingReconnect) {
          send('audioSegment', sendToSocket);
        }
        if (services.Nrt && !replayAbandoned) {
          // Key on the phraseIndex parameter, not payload.phraseIndex (which is
          // only populated via the ...metaObj spread) -- more robust to callers
          // that don't happen to put phraseIndex on metaObj/recordingProps.
          const pi = phraseIndex;
          if (pi !== currentBufferedPhraseIndex || testID !== currentBufferedActivityId) {
            if (pendingReplay) {
              // The client has moved to a new phrase (or activity) while an
              // older phrase's replay was still pending -- that phrase's fate
              // is unknown (never acked before we moved on), and merging its
              // audio with the new phrase's buffer would replay two phrases
              // with no boundary. Discard the old one rather than corrupt the
              // new one (confirmed 2026-07 with the PR author).
              clearPendingReplay(
                testID !== currentBufferedActivityId
                  ? 'activity_changed_while_pending'
                  : 'phrase_advanced_while_disconnected'
              );
            }
            phraseReplayBuffer = [];
            currentBufferedPhraseIndex = pi;
            currentBufferedActivityId = testID;
            // The stop suppressed by the OLD phrase's done+stopAll (if any)
            // belongs to the audio just discarded above -- letting it survive
            // would finalize the NEW phrase with a stop meant for a different
            // one once replayed on reconnect (Cursor Bugbot, "Stop leaks
            // across phrase advance", post-review of aa118f15a, 2026-07-17).
            // Intentionally tied to this Nrt-gated branch, same as
            // phraseReplayBuffer itself -- a phrase advance signaled by a
            // segment without services.Nrt doesn't reach here either.
            pendingStop = false;
          }
          phraseReplayBuffer.push({ payload: payload, services: { Nrt: true } });
        }
        if(services.SpeechStream){
          totalBytesSent += audioStream.size - 44;
          // Log first audio segment sent for each new phrase (skip kickstart segments)
          if(phraseIndex !== undefined && phraseIndex !== lastPhraseIndex && !(metaObj && metaObj.isKickstartSegment)){
            logSessionInfo(`initial_audio_segment_sent_phrase_${phraseIndex}`,{
              timeStamp: Date.now(),
              sequenceNumber,
              audioSize: audioStream.size - 44
            });
            lastPhraseIndex = phraseIndex;
          }
          sequenceNumber++;
          
        }
        if (services.PartialSpeechStream) {
          adhocSequence[audioDestination]++;
        }
      }

      if(pauseRequested){
        sendPause();
        pauseRequested = false;
      }
    }
  }

  if(metaObj.done){
    stopAll();
  }
}

function processSpeechEvent(message,payload){
  // Log incoming socket event
  socketLogger.logIncoming(message, payload, payload?.service);
  
  switch(message){
    case "decoded": {
      // Kaldi vs Spanish-pilot placement: dispatch onDecoded here; excluded from catch-all below
      switch (payload.service) {
        case 'PlacementManager':
          if (DEBUG_SOCKET) console.log('%cPlacementManager', 'background: #00a1c9; color: #fff; padding: 1em;', payload.result);
          if (!useGoogleNRT && !useSpanishPlacement) onDecoded(payload);
          break;
        case 'AmiraPlacementManager':
          if (DEBUG_SOCKET) console.log('%cAmiraPlacementManager', 'background: #00a1c9; color: #fff; padding: 1em;', payload.result);
          if (!useGoogleNRT && useNewSpanishPlacementManagerMode) onDecoded(payload);
          else if (useSpanishPlacement && DEBUG_SOCKET) {
            console.warn('[SpeechStream] AmiraPlacementManager decoded while useNewSpanishPlacementManagerMode is false; ignoring (catch-all also skips this service)');
          }
          break;
        case 'Wav2VecPhonASR':
          // Phoneme ASR must always reach onDecoded for intervention phoneme
          // listening, regardless of useSpanishPlacement. The catch-all below
          // excludes it when Spanish placement is active.
          if (!useGoogleNRT) onDecoded(payload);
          break;
        default:
          break;
      }
      if(payload.service === "PhonPlacementManager"){
        if(DEBUG_SOCKET) console.log('%cPhonPlacementmanager', 'background: #00a1c9; color: #fff; padding: 1em;', payload.result.text);
      }
      if(payload.service === "Wav2VecStreamV2"){
        if(DEBUG_SOCKET) console.log('%cWav2VecStreamV2', 'background: #00a1c9; color: #fff; padding: 1em;', payload.result.text);
      }
      // Spanish legacy + Google: onDecoded via parseGooglePayload. New Spanish PM uses AmiraPlacementManager instead.
      if(payload.service === "GoogleSpeechStream" || payload.service === "GooglePlacementManager"){
        if(useSpanishPlacement && !useNewSpanishPlacementManagerMode){
          if(payload && payload.data && payload.data.results){
            let dummyPayload = parseGooglePayload(payload.data.results,true)
            onDecoded(dummyPayload)
          }
        }
        if(DEBUG_SOCKET) console.log('%cGooglePlacementManager', 'background: #00a1c9; color: #fff; padding: 1em;', payload.result.text);
      }
      // PlacementManager, AmiraPlacementManager, and Wav2VecPhonASR handled explicitly above; exclude from catch-all
      if(!useGoogleNRT && !useSpanishPlacement && payload.service !== 'AmiraPlacementManager' && payload.service !== 'PlacementManager' && payload.service !== 'Wav2VecPhonASR'){
        onDecoded(payload);
      }

      break;
    }
    case "asrOutput":
      onASROutput(payload);
      break;
    case "prediction":
      phraseReplayBuffer = [];
      onPrediction(payload);
      break;
    case "saved":
      onSaved(payload);
      break;
    case "GoogleSpeechIntermediate":
      if(useGoogleNRT && payload && payload.results){
        let dummyPayload = parseGooglePayload(payload.results,true);
        onDecoded(dummyPayload)
      }
      break;
    case "_error":
      if(DEBUG_SOCKET) console.log('%cSocket Error', 'background: #c00; color: #fff; padding: 1em;', JSON.stringify(payload, null, 0));
      break;
    case "onDetectorsEnd":
      onDetectorsEnd(payload)
      break;
    case "comprehensionInference":
      onComprehensionInference(payload)
      break;
    case "comprehensionInferenceError":
      console.log("Comprehension Dialog: Comprehension Inference Error",payload)
      onComprehensionInferenceError(payload);
      break;
    case "kaldiConnected":
      console.log('ASR Connected', { timestamp: Date.now() });
      logSessionInfo('kaldi_connected', {});
      // Dispatch custom event for Speech.js to listen to
      const event = new CustomEvent('asrConnected', { detail: { timestamp: Date.now() } });
      window.dispatchEvent(event);
      break;
    default:
      console.log("UNHANDLED MESSAGE",message);
  }
}

function parseGooglePayload(results,concatFinal){
  if(results && results[0] && results[0].alternatives[0]){
    if(results[0].isFinal && concatFinal){
      transcript += results[0].alternatives[0].transcript;
      results[0].alternatives[0].transcript = '';
    }
    let dummyPayload = {service: "PlacementManager", result: {text: results[0].alternatives[0].transcript}};
    return dummyPayload;
  }
}
function clearStream(){
  //Queuing for the speech stream
  streamQueue = [];
  headPointer = 0;
  sequenceNumber = 0;
  buildSequence = 0;
  sending = false;
}

// Unified build retry mechanism
function clearBuildRetryTimeout(retryState) {
  if (retryState.timeout) {
    clearTimeout(retryState.timeout);
    retryState.timeout = null;
  }
}

function scheduleBuildRetry(retryState) {
  clearBuildRetryTimeout(retryState);
  
  retryState.timeout = setTimeout(() => {
    if (!retryState.acknowledged && retryState.retryCount < MAX_BUILD_RETRIES && retryState.currentRequest) {
      retryState.retryCount++;
      
      const logData = {
        retryCount: retryState.retryCount,
        timestamp: Date.now(),
        previousSequenceNumbers: [...retryState.sequenceNumbers]
      };
      
      let newSequenceNumber;
      let retryRequest;
      
      if (retryState.type === BUILD_TYPE_REGULAR) {
        // Regular build retry logic
        sequenceNumber++;
        newSequenceNumber = sequenceNumber;
        
        buildSequence = newSequenceNumber; // Update for legacy compatibility
        logSessionInfo("build_retry_attempt", logData);
        
      } else if (retryState.type === BUILD_TYPE_ADHOC) {
        // Adhoc build retry logic
        if (!adhocSequence[audioDestination]) {
          adhocSequence[audioDestination] = 0;
        }
        adhocSequence[audioDestination]++;
        newSequenceNumber = adhocSequence[audioDestination];
        
        buildAdhocSequence = newSequenceNumber; // Update for legacy compatibility
        logSessionInfo("adhoc_build_retry_attempt", {
          ...logData,
          audioDestination
        });
      }
      
      // Track the new sequence number
      retryState.sequenceNumbers.push(newSequenceNumber);
      retryRequest = {
        ...retryState.currentRequest,
        payload: {
          ...retryState.currentRequest.payload,
          sequenceNumber: newSequenceNumber,
          retryAttempt: retryState.retryCount,
          created: Date.now()
        }
      };
      
      // Send the retry request
      emit(socket, 'audioSegment', retryRequest);
      
      if(socket){
        const logEvent = retryState.type === BUILD_TYPE_REGULAR ? "build_audio_retry_sent" : "adhoc_build_audio_retry_sent";
        const logData = {
          timestamp: Date.now(),
          sequenceNumber: newSequenceNumber,
          retryCount: retryState.retryCount
        };
        
        if (retryState.type === BUILD_TYPE_ADHOC) {
          logData.audioDestination = audioDestination;
        }
        
        logSessionInfo(logEvent, logData);
      }
      
      // Schedule next retry if this one fails
      scheduleBuildRetry(retryState);
      
    } else if (!retryState.acknowledged && retryState.retryCount >= MAX_BUILD_RETRIES) {
      // Max retries reached, give up
      const logEvent = retryState.type === BUILD_TYPE_REGULAR ? "build_retry_max_attempts_reached" : "adhoc_build_retry_max_attempts_reached";
      const logData = {
        retryCount: retryState.retryCount,
        timestamp: Date.now(),
        allSequenceNumbers: [...retryState.sequenceNumbers]
      };
      
      if (retryState.type === BUILD_TYPE_ADHOC) {
        logData.audioDestination = audioDestination;
      }
      
      logSessionInfo(logEvent, logData);
      clearBuildRetryTimeout(retryState);
      retryState.currentRequest = null;
    }
  }, BUILD_RETRY_TIMEOUT_MS);
}


// requests the combination of stored/available audio files
export function buildSessionAudio (testID,metaObj) {
  // Clear any existing retry mechanism and set type
  clearBuildRetryTimeout(buildAudioState);
  buildAudioState.retryCount = 0;
  buildAudioState.sequenceNumbers = [];
  buildAudioState.acknowledged = false;
  buildAudioState.type = BUILD_TYPE_REGULAR;
  
  let buildAudioRequest =
  {
    services: { SpeechStream: true },
    version: '2.0.1',
    payload: {
      id: testID,
      activityId: testID,
      build: true,
      data: null,
      created: Date.now(),
      sequenceNumber,
      ...metaObj
    },
  }

  // Store the request for potential retries
  buildAudioState.currentRequest = buildAudioRequest;
  
  buildSequence = sequenceNumber;
  buildAudioState.sequenceNumbers = [sequenceNumber]; // Track this sequence number
  buildAcknowledged = false;
  
  //###console.log("BUILD SENT",buildSequence,Date.now());
  emit(socket, 'audioSegment', buildAudioRequest);
  
  if(socket){
    logSessionInfo("build_audio_sent",{
      timestamp: Date.now(),
      sequenceNumber,
      retryCount: 0
    });
    
    // Schedule retry mechanism
    scheduleBuildRetry(buildAudioState);
  }else{
    logSessionInfo("build_audio_not_sent",{reason: "no_socket",timestamp: Date.now()});
  }
}

export function buildAdhocAudio(testID, metaObj) {
  if (!audioDestination) {
    console.error('buildAdhocAudio called without audioDestination');
    return;
  }
  
  // Clear any existing retry mechanism and set type
  clearBuildRetryTimeout(buildAudioState);
  buildAudioState.retryCount = 0;
  buildAudioState.sequenceNumbers = [];
  buildAudioState.acknowledged = false;
  buildAudioState.type = BUILD_TYPE_ADHOC;
  
  const sequenceNumberToUse = adhocSequence[audioDestination];
  let buildAudioRequest = {
    services: { PartialSpeechStream: true },
    version: '2.0.1',
    payload: {
      id: testID,
      activityId: testID,
      build: true,
      data: null,
      created: Date.now(),
      sequenceNumber: sequenceNumberToUse,
      audioDestination,
      ...metaObj
    }
  }

  // Store the request for potential retries
  buildAudioState.currentRequest = buildAudioRequest;
  
  buildAdhocSequence = sequenceNumberToUse;
  buildAudioState.sequenceNumbers = [sequenceNumberToUse]; // Track this sequence number
  buildAdhocAcknowledged = false;
  
  emit(socket, 'audioSegment', buildAudioRequest);
  
  if(socket){
    logSessionInfo("adhoc_build_audio_sent",{
      timestamp: Date.now(),
      audioDestination,
      sequenceNumber: sequenceNumberToUse,
      retryCount: 0
    });
    
    // Schedule retry mechanism
    scheduleBuildRetry(buildAudioState);
  }else{
    logSessionInfo("adhoc_build_audio_not_sent",{reason: "no_socket", audioDestination, timestamp: Date.now()});
  }
}

// Clean audio URL from {data=https://...} format to just the URL
export function cleanAudioUrl(audioUrl) {
  if (!audioUrl) return null;

  // Handle the case where audio.url contains {data=https://...}
  if (typeof audioUrl === 'string' && audioUrl.startsWith('{data=')) {
    const urlMatch = audioUrl.match(/data=(https:\/\/[^}]+)/);
    if (urlMatch) {
      return urlMatch[1];
    }
  }

  // Return the URL as-is if it's already in the correct format
  return audioUrl;
}

// retrieves a bundled session audio file
export function searchSessionAudio (testID) {
  return studentRecordService.getActivity(testID)
    .then((response) => {
      console.log('SEARCH Audio response: ', response);

      const activity = response.data.getActivity[0];
      if (activity && activity.audio && activity.audio.url) {
        const audioUrl = activity.audio.url;
        const cleanedUrl = cleanAudioUrl(audioUrl);
        console.log('SEARCH Audio URL: ', cleanedUrl);
        return cleanedUrl;
      }

      return null;
    })
    .catch(err => {
      console.log('SEARCH Audio failed: ', err);
      return null;
    });
}

//Queuing for the speech stream
let streamQueue = [];
let sending = false;
const numRetries = 10;
let headPointer = 0;

//Throws a packet in the queue and kicks off a send if we're not already sending
// If we are sending, the sendPacket function will pick this up when it's done.
// Note: we don't dequeue when we send the packet until successful, hence the peek and not dequeue
function queuePacket(inPacket){
  streamQueue.push(inPacket);
  //Leave in for debugging console.log("PUSHED",streamQueue.length,headPointer,inPacket.body.sequenceNumber);
  if(!sending){
    sending = true;
    sendPacket(peekNextPacket(),numRetries);
  }
}

function dequeuePacket(){
  //Leave in for debugging console.log("DEQUEUING",streamQueue.length,headPointer,streamQueue[headPointer] ? streamQueue[headPointer].body.sequenceNumber : "NULL");
  let retValue = streamQueue[headPointer];
  headPointer++;
  return retValue;
}

//Get the next packet without dequeueing
function peekNextPacket(){
  if(headPointer < streamQueue.length){
    return streamQueue[headPointer];
  }else{
    return null;
  }
}

export function isSendingComplete(){
  return (buildSequence && buildAcknowledged && acknowledgedSequence >= (buildSequence-1));
}

export function isAdhocSendingComplete(){
  return (buildAdhocSequence && buildAdhocAcknowledged && adhocAcknowledgedSequence >= (buildAdhocSequence-1));
}

export function sendingProgress(){
  return buildSequence ? Math.round(100*acknowledgedSequence/buildSequence) : 0;
}

//Sends the next packet, dequeues if successful, and picks up any
// new packets that have been queued in the interim.
function sendPacket(inPacket, retries){
  let apiName = awsmobile.aws_cloud_logic_custom[1].name;
  let path = '/stream';

  if(!inPacket){
    return Promise.resolve();
  }
  else{
    //Leave in for debugging console.log("SENDING",inPacket.body.sequenceNumber);
  }

  API.post(apiName, path, inPacket)
  .then(resp => {
    //On a successful post, dequeue the previous packet and send the next
    let sentPacket = dequeuePacket();
    if(sentPacket && sentPacket.body &&!sentPacket.body.data){
      //Leave in for debugging console.log("LAST PACKET!");
      clearStream();
    }else{
      let nextPacket = peekNextPacket();
      if(nextPacket){
        sendPacket(nextPacket,numRetries);
      }else{
        sending = false;
      }
    }
  })
  .catch(err => {
    console.log('Stream packet failed:', err,inPacket.body.id,inPacket.body.meta); //Leave in for debug
    if(retries && inPacket){
      console.log("Retrying speech stream:",retries); //Leave in for debug
      sendPacket(inPacket,retries-1);
    }else{
      dequeuePacket();
      sending = false;
      throw new Error(err);
    }
  });
}

export function setNetworkMonitoringService(nms) {
  networkMonitoringService = nms;
}

/**
 * Starts a cooldown on the network monitoring service, suppressing analysis
 * without changing activation state. Used when audio device changes would
 * produce misleading queue data (e.g. headset unplug triggering recorder reinit).
 * @param {number} durationMs - Cooldown duration in milliseconds
 */
export function startNetworkMonitoringCooldown(durationMs) {
  if (networkMonitoringService) {
    networkMonitoringService.startCooldown(durationMs);
  }
}

// --- Local ASR lifecycle (TT-828) ---

/**
 * Starts the phrase-scoped local ASR lifecycle at the recording metadata boundary.
 * Called from Speech.enableRecording() with the current recording props.
 * @param {object} meta - Phrase metadata from ReaderManager.getRecordingProps()
 * @returns {boolean} true when local ASR owns this phrase
 */
export function startLocalAsrPhrase(meta = {}) {
  const hasMeta = hasLocalAsrPhraseMeta(meta);
  if (!hasMeta) {
    // Not a phrase that lost local ASR: this is the pre-enableRecording probe
    // hasLocalAsrPhraseMeta exists to reject, so it is deliberately not counted.
    setLocalAsrPlacementRoute(LOCAL_ASR_PLACEMENT_ROUTE.SERVER);
    return false;
  }

  // tutor upAndDown phrases score against server-Kaldi phoneme tokens
  // ("ch_phon ih_phon k_phon chick"). Local ASR cannot produce those tokens, so
  // arming it here decodes the phoneme sounds to <UNK> and strands the cursor on the
  // phoneme portion (TT-1285). Keep server Kaldi as the single owner of the phrase —
  // mid-phrase ASR switching is unsafe (TT-937), so the whole phrase stays on server.
  if (meta.usesKaldiPhonemePlacement) {
    recordLocalAsrServerPhrase(LOCAL_ASR_FALLBACK_REASON.PHONEME_PLACEMENT, meta);
    setLocalAsrPlacementRoute(LOCAL_ASR_PLACEMENT_ROUTE.SERVER);
    return false;
  }

  if (localAsrActivityFailed) {
    // Already reported at the latch (placement.activityFellBackToServer), so counted
    // only — runtime.activityEnded carries how many phrases the server ended up serving.
    localAsrPhraseCounts.server += 1;
    // Give these phrases a reason too: without it a failed activity's row reads
    // serverPhrases > 0 with a nonLocalPhraseReasons that omits the latch — a "server
    // phrases, no reason" gap in the flat breakdown. The set is deduped, so this is one
    // entry, matching activityFailureReason on the same row.
    localAsrReportedFallbacks.add(LOCAL_ASR_FALLBACK_REASON.INFERENCE_FAILURE);
    return false;
  }

  // The warmup capability gate (TT-1169): a session whose measured verdict routed
  // placement to the server never arms a local phrase, however healthy the runtime
  // looks. The decision is the orchestrator's (it is what makes its
  // shouldUseLocalAsr answer server); this is the phrase-level enforcement, kept
  // here for its telemetry — the orchestrator answer alone cannot say "and this
  // activity's phrases were actually gated". Checked per phrase rather than
  // latched at decision time so a LATE verdict — a run that outlived the
  // story-launch deadline and settled after phrase 0 was already underway —
  // applies from the next phrase, and checked ahead of the readiness probe below
  // because "measured too slow" is the truer answer than "runtime not ready" on a
  // session that has both. An undecided session falls through to the eligibility
  // routing below unchanged.
  const warmupRouteDecision = getLocalAsrWarmupRouteDecision();
  if (warmupRouteDecision && warmupRouteDecision.route === LOCAL_ASR_ROUTE.SERVER) {
    recordLocalAsrWarmupGatedPhrase(meta);
    setLocalAsrPlacementRoute(LOCAL_ASR_PLACEMENT_ROUTE.SERVER);
    return false;
  }

  // Provisional pessimism while an OVERRUN run is still settling (TT-1218): the slowest
  // devices in the fleet used to read exactly ONE phrase on local ASR before the gate
  // they had just earned took effect, and it was the worst phrase to lose — it decodes
  // while the warmup's in-flight iteration is still unwinding.
  //
  // The condition, and why each half of it is load-bearing, lives with the state in
  // CursorOrchestrator.isLocalAsrProvisionallyServerRouted. Enforced HERE as well as
  // there because this gate is the only place that has to distinguish the two reasons a
  // phrase was gated: the orchestrator's answer says a phrase went to the server, not
  // whether a verdict or an unsettled overrun sent it, and the timeline needs that split
  // (see recordLocalAsrWarmupGatedPhrase). The connection waits, which only need to know
  // WHERE the phrase is going, ask shouldUseLocalAsrForNextPhrase instead.
  //
  // Nothing here switches a phrase that has already armed; handing the server a partial
  // phrase is the TT-937 truncated-scoring failure, and next-phrase remains the correct
  // boundary for an armed phrase. This is only about the phrase that has not armed yet.
  if (isLocalAsrProvisionallyServerRouted()) {
    recordLocalAsrWarmupGatedPhrase(meta, { provisional: true });
    setLocalAsrPlacementRoute(LOCAL_ASR_PLACEMENT_ROUTE.SERVER);
    return false;
  }

  // TT-1283: the end-of-phrase preparation (ReaderManager) determined this phrase has no
  // usable biased graph — its per-phrase HCLG never downloaded even after the
  // between-phrase re-attempt, its FST failed to build, or the runtime cannot bind a
  // graph. The Vosk word-list grammar fallback is defective (zoov relabel), so route the
  // phrase to server Kaldi rather than decode it wrong locally. Per phrase, not the
  // activity latch — the next phrase re-evaluates and can return to local. Decided at the
  // source (ReaderManager's end-of-phrase prep) and honored here, mirroring
  // usesKaldiPhonemePlacement. Placed after the warmup/provisional gates so a session
  // already routed to server for a capability reason keeps that reason.
  if (meta.forceServerPlacement) {
    recordLocalAsrServerPhrase(LOCAL_ASR_FALLBACK_REASON.GRAPH_UNAVAILABLE, meta);
    setLocalAsrPlacementRoute(LOCAL_ASR_PLACEMENT_ROUTE.SERVER);
    return false;
  }

  if (!localAsr || !localAsr.isReady) {
    recordLocalAsrServerPhrase(LOCAL_ASR_FALLBACK_REASON.RUNTIME_NOT_READY, meta);
    setLocalAsrPlacementRoute(LOCAL_ASR_PLACEMENT_ROUTE.SERVER);
    return false;
  }

  // activityId is undefined on phrase 0 in tutor mode (recording props only carry it
  // in assessment mode), which stripped local ASR's phrase-0 telemetry of its key
  // (TT-1237). Fall back to the session's current/user activity so every phrase's
  // metadata is attributable.
  const activityId = meta.activityId || currentActivityId || getUserData()?.activityId;

  const started = localAsr.startPhrase({
    ...meta,
    activityId,
    phraseIndex: meta.phraseIndex,
    expectedText: meta.expectedText,
    wordList: meta.wordList,
  });

  if (started) {
    localAsrPhraseCounts.local += 1;
    setLocalAsrPlacementRoute(LOCAL_ASR_PLACEMENT_ROUTE.LOCAL);
  } else {
    recordLocalAsrServerPhrase(LOCAL_ASR_FALLBACK_REASON.HANDLER_DECLINED, meta);
    setLocalAsrPlacementRoute(LOCAL_ASR_PLACEMENT_ROUTE.SERVER);
  }
  return Boolean(started);
}

/**
 * Awaited readiness check for one phrase's biased graph (TT-1283), used by
 * ReaderManager's end-of-phrase preparation to decide whether the NEXT phrase decodes
 * locally or must route to server Kaldi. Delegates to the handler's
 * prepareBiasedGraphForPhrase, which resolves (and thereby load-bearing WARMS) the
 * phrase's recognizer and returns whether it will decode biased.
 *
 * Resolves true (leave the phrase local) when there is no handler — a server session or
 * a backend without the method has no defective word-list path to route around.
 * @param {object} metadata Phrase metadata carrying storyId/phraseIndex.
 * @param {{speculative?: boolean}} [options] Forwarded to the warm for correct telemetry
 *   labeling (true for a next-phrase prep that may never be read).
 * @returns {Promise<boolean>} false only when a Vosk phrase would fall to the word-list grammar.
 */
export function prepareLocalAsrPhraseGraph(metadata, options = {}) {
  if (!localAsr || typeof localAsr.prepareBiasedGraphForPhrase !== 'function') {
    return Promise.resolve(true);
  }
  return localAsr.prepareBiasedGraphForPhrase(metadata, options);
}

/**
 * Initializes the local ASR runtime once per session. Dispatches a synthetic
 * asrConnected event on success so WAIT_FOR_ASR_CONNECTION flows can proceed.
 * On failure, marks local ASR unavailable so server placement stays enabled.
 *
 * Callers are responsible for gating on asset readiness and recording mode
 * before invoking; this function only checks the device/flag enablement via
 * isLocalAsrEnabled().
 * @returns {Promise<boolean>}
 */
export function initLocalPlacementAsr() {
  if (!isLocalAsrEnabled()) {
    return Promise.resolve(false);
  }

  // CR-10679: after an activity hard-failure the engine is gone and must NOT come back
  // as a background boot at the next activity's start (memory spike over a live
  // session on 4 GB devices). The session stays on server ASR; App's gated boot is the
  // only path that re-arms this (ensureLocalPlacementAsrBooted). Checked BEFORE the
  // isReady shortcut: the failure dispose nulls localAsr only after its await, so a
  // straggling reference could otherwise still answer "ready" here.
  if (localAsrRetiredAfterActivityFailure) {
    recordLocalAsrLifecycle('runtime.initSkipped', { reason: 'retiredAfterActivityFailure' });
    return Promise.resolve(false);
  }

  if (localAsr && localAsr.isReady) {
    return Promise.resolve(true);
  }

  // localAsrInitializing: a boot is already constructing an engine. localAsrSettlingInitPromise:
  // a disposed-mid-createModel boot is still tearing down its (uninterruptible) handler.
  // Constructing another engine in either window puts two on the device at once (CR-10679).
  // The gated boot (ensureLocalPlacementAsrBooted) awaits the settle before it reaches here,
  // so this only turns away the legacy background callers, which is exactly right.
  if (localAsrInitializing || localAsrSettlingInitPromise) {
    return Promise.resolve(false);
  }

  const run = runLocalPlacementAsrInit();
  localAsrInitPromise = run;
  run.finally(() => {
    if (localAsrInitPromise === run) {
      localAsrInitPromise = null;
    }
  }).catch(() => {});
  return run;
}

/**
 * The awaitable boot behind App's loading gate (TT-1313 / CR-10679). Resolves true once
 * the shared engine is ready — immediately if it already is, on the in-flight init's
 * settle if one is running, else by starting one. Never rejects (init failure resolves
 * false; the caller falls back to server ASR). Clears the activity-failure retirement:
 * an app-level boot is an explicit decision that local ASR is back on the table.
 * @returns {Promise<boolean>}
 */
export async function ensureLocalPlacementAsrBooted() {
  // Snapshot before the waits below. Every dispose bumps this id, so if it moves across our
  // awaits the App-side gate that requested this boot gave up mid-wait — its bounded timeout
  // fired and it already fell back to server ASR (and disposed). Booting on regardless would
  // resume after the settle and construct an ORPHAN engine: a WASM heap resident for a whole
  // server-ASR session that never uses it — one engine, not the CR-10679 double, but just as
  // unbudgeted and invisible past runtime.initSucceeded (PR #6006 review #2). This is reachable
  // in the exact classroom case the fix targets, because A's remaining download can outlast B's
  // timeout — and A's dispose can itself block on handler.dispose() until createModel resolves,
  // so B may spend its wait in EITHER await; snapshotting at entry covers both. The dispose that
  // PARKED the settling init ran before this line, so it is already in the baseline and is not
  // counted as a give-up.
  //
  // Precision note: the id also moves when a fresh init STARTS, so the check below really means
  // "a dispose OR an init landed", not dispose-only. That is exact today only because there is a
  // single gated-boot caller (App, one per instance) — the Speech component warms via the legacy
  // initLocalPlacementAsr, which the localAsrSettlingInitPromise guard blocks from bumping the id
  // mid-wait. If a SECOND ensureLocalPlacementAsrBooted caller is ever added, two boots parked on
  // the same settling promise would see this move when the first resumes and starts the engine,
  // and the second would return false instead of joining the in-flight init (a benign fall-back
  // to server ASR, never a second engine). Make it dispose-only then — either move this check
  // below the localAsrInitPromise join, or key it off a dispose-only counter (PR #6006 review #2).
  const lifecycleIdBeforeWaits = localAsrLifecycleId;
  // A dispose still settling (activity-failure teardown, a logout that this login
  // follows) nulls localAsr only after its await; deciding on the stale reference would
  // report "ready" on an engine whose worker is being terminated.
  if (localAsrDisposePromise) {
    // runDisposeLocalPlacementAsr never rejects; the catch keeps this contract true even
    // if that ever changes, since a rejection here would escape into App's gate.
    await localAsrDisposePromise.catch(() => {});
  }
  // A boot the dispose above doomed may still be parked in createModel (it cannot be
  // interrupted). Wait for it to settle and dispose its handler before starting a fresh one,
  // so the two engines never coexist (CR-10679). We do NOT return its result — it can only
  // resolve false on the lifecycle mismatch — but starting fresh AFTER it settles means, if
  // its download finished inside this budget, the model is already in IDBFS and the fresh
  // boot is warm. App's Promise.race bounds this wait so a truly stuck createModel still
  // falls back to server ASR.
  if (localAsrSettlingInitPromise) {
    await localAsrSettlingInitPromise.catch(() => {});
  }
  // The lifecycle id moved while we waited (see the precision note above — a dispose today):
  // the requester abandoned this boot, so do not construct. Cleared BEFORE the retirement
  // latch below so an abandoned boot does not lift retirement without booting.
  if (localAsrLifecycleId !== lifecycleIdBeforeWaits) {
    return false;
  }
  // Only now that the boot will actually proceed: an app-level boot is the explicit decision
  // that local ASR is back on the table, so lift the activity-failure retirement. Kept below
  // the abandon bail-out (not at function entry) so a boot the requester gave up on mid-wait
  // does NOT re-arm the legacy background init path mid-session — the CR-10679 behavior the
  // latch exists to prevent (PR #6006 review #6).
  localAsrRetiredAfterActivityFailure = false;
  if (localAsr && localAsr.isReady) {
    return true;
  }
  if (localAsrInitPromise) {
    return localAsrInitPromise;
  }
  return initLocalPlacementAsr();
}

/**
 * The boot itself; only initLocalPlacementAsr calls this, after the guards above.
 * @returns {Promise<boolean>}
 */
async function runLocalPlacementAsrInit() {

  localAsrInitializing = true;
  const initLifecycleId = localAsrLifecycleId + 1;
  localAsrLifecycleId = initLifecycleId;
  const initStartedAt = Date.now();
  recordLocalAsrLifecycle('runtime.initStarted', { lifecycleId: initLifecycleId });
  let handler = null;

  try {
    handler = new LocalPlacementAsrHandler();
    localAsr = handler;

    handler.onTranscript((text) => {
      onDecoded({
        service: 'PlacementManager',
        source: 'local-asr',
        result: { text },
      });
    });

    handler.onInterventionTranscript((text) => {
      onDecoded({
        service: 'PlacementManager',
        source: 'local-asr',
        result: { text },
      });
    });

    handler.onError((error) => {
      recordLocalAsrFailure('runtime.error', {
        reason: error?.reason,
        mode: error?.mode,
        count: error?.count,
        error: error?.error?.message || String(error?.error || ''),
      });
      if (error.reason === LOCAL_ASR_FALLBACK_REASON.INFERENCE_FAILURE) {
        fallbackLocalAsrActivity(LOCAL_ASR_FALLBACK_REASON.INFERENCE_FAILURE, { count: error?.count, mode: error?.mode });
      }
    });

    // Per-phrase raw transcript timeline, written as its own object rather than folded
    // into the lifecycle timeline: it is high-volume, phrase-scoped, and shaped for
    // direct replay through scripts/transcript-simulator.
    handler.onPhraseTranscriptLog((phraseLog) => {
      logSessionInfo(`local_asr_raw_phrase_${phraseLog.phraseIndex}`, phraseLog);
    });

    const success = await handler.init();

    if (localAsrLifecycleId !== initLifecycleId || localAsr !== handler) {
      await disposeStaleLocalAsrHandler(handler);
      return false;
    }

    if (success) {
      recordLocalAsrLifecycle('runtime.initSucceeded', {
        lifecycleId: initLifecycleId,
        durationMs: Date.now() - initStartedAt,
        backend: handler.backend,
        modelVersion: handler.modelVersion,
      });
      // Warmup does NOT start here. It used to, and the ordering against
      // dispatchEvent below was load-bearing for that: a synchronous asrConnected
      // listener calling startPhrase() → cancelWarmup() had to set the flag before
      // the warmup loop resumed from its first yield. Warmup now runs at story load
      // (warmLocalAsrForStory), after the per-story .fst download and before the
      // activity launches, so nothing here can race it and the ordering constraint
      // is retired rather than preserved. Measuring at init was also measuring the
      // wrong moment: on the TT-1158 repro device warmup finished at +36.0s while
      // the graph it was meant to represent did not land until +68.1s (TT-1209).
      const event = new CustomEvent('asrConnected', {
        detail: { timestamp: Date.now(), source: 'local-asr' },
      });
      window.dispatchEvent(event);
    } else {
      recordLocalAsrFailure('runtime.initFailed', {
        lifecycleId: initLifecycleId,
        durationMs: Date.now() - initStartedAt,
      });
      // Clear localAsr so disposeLocalPlacementAsr() won't double-dispose a
      // handler whose runtime was already cleaned up by disposeStaleLocalAsrHandler.
      // The lifecycle guard (localAsrLifecycleId) also protects, but this avoids
      // relying on a timing-dependent ID check for correctness.
      localAsr = null;
      await disposeStaleLocalAsrHandler(handler);
    }

    return success;
  } catch (error) {
    recordLocalAsrFailure('runtime.initThrew', {
      lifecycleId: initLifecycleId,
      durationMs: Date.now() - initStartedAt,
      error: error?.message,
    });
    if (localAsr === handler) {
      localAsr = null;
    }
    await disposeStaleLocalAsrHandler(handler);
    return false;
  } finally {
    if (localAsrLifecycleId === initLifecycleId) {
      localAsrInitializing = false;
    }
    // Runtime-init checkpoint. Init normally resolves before the first activity, so
    // this is still a pre-activity flush keyed by user; the activity-start flush
    // re-emits the same timeline under the activity.
    flushLocalAsrLifecycle('runtimeInitSettled');
  }
}

/**
 * Releases a handler whose async init completed after a newer lifecycle took over.
 * This prevents init/dispose races from leaving an orphaned ONNX runtime alive.
 * @param {LocalPlacementAsrHandler} handler
 * @returns {Promise<void>}
 */
async function disposeStaleLocalAsrHandler(handler) {
  if (!handler || typeof handler.dispose !== 'function') {
    return;
  }

  try {
    await handler.dispose();
  } catch (error) {
    recordLocalAsrFailure('runtime.staleDisposeFailed', { error: error?.message });
  }
}

/**
 * Whether local ASR has real phrase metadata to start from. This prevents
 * runtime initialization from starting an empty phrase before enableRecording runs.
 * @param {object} meta
 * @returns {boolean}
 */
function hasLocalAsrPhraseMeta(meta = {}) {
  return [
    meta.activityId,
    meta.storyId,
    meta.phraseId,
    meta.phraseIndex,
    meta.expectedText,
  ].some((value) => value !== undefined && value !== null && value !== '');
}

/**
 * Ends the local ASR activity lifecycle WITHOUT tearing down the runtime.
 *
 * endUniversalSocketSession runs at the end of EVERY activity (ReaderManager
 * finalizeAssessment/finalizeTutor), and disposing there threw away the Vosk
 * worker — its WASM JIT tier-up, the loaded model, and the recognizer caches —
 * between back-to-back stories, so every new story re-paid the full cold start
 * the warmup exists to hide. Keep a healthy runtime alive across activities
 * (per-activity route/phrase state still resets); Speech unmount and logout
 * still dispose it via disposeLocalPlacementAsr.
 *
 * A runtime that latched an activity-level inference failure IS disposed so the
 * next activity re-inits a fresh worker instead of inheriting a wedged one —
 * matching the recovery behavior the per-activity dispose used to provide.
 * Synchronous by contract: endUniversalSocketSession calls this without awaiting, so
 * suspending here would let the next activity's first phrase arm (and increment the
 * counts) before the reset below wipes them.
 * @returns {void}
 */
export function resetLocalAsrForNextActivity() {
  // Every one of these snapshots MUST be captured before the teardown block below, and
  // everything after it MUST read from them rather than from the module-level fields.
  // Teardown mutates the very state they describe: disposeLocalPlacementAsr zeroes
  // localAsrPhraseCounts, localAsrPlacementRoute, and localAsrActivityFailed
  // synchronously before its first await, and localAsrTouchedActivity reads all three.
  // Reading a module field inside the `if (touched)` block instead would silently log
  // zeros / SERVER / false.
  //
  // `touched` gates on local ASR having been part of this session at all. This function
  // runs at the end of EVERY activity, including on devices and sessions where local ASR
  // is off; recording an event there would make the timeline non-empty and so write a
  // local_asr_lifecycle object per activity for a session local ASR was never part of,
  // breaking the "server-ASR sessions stay quiet" guarantee. The disabled session still
  // gets its one enablement event, written at the preload checkpoint.
  const touched = localAsrTouchedActivity();
  const activityEndedDetail = touched ? {
    activityId: getUserData()?.activityId,
    activityFailed: localAsrActivityFailed,
    // The activity-level hard-failure latch reason (only INFERENCE_FAILURE reaches it
    // today), or null when the activity did not hard-fail. Named for what it IS: a null
    // here means "the activity did not fail", NOT "no phrase fell back" — a healthy null
    // can sit beside serverPhrases > 0. "Why a phrase did not run locally" is the separate
    // nonLocalPhraseReasons below.
    activityFailureReason: localAsrActivityFallbackReason,
    // The deduped set of reasons phrases did not run on local ASR this activity:
    // runtimeNotReady / handlerDeclined (fell back), phraseWarmupGated[Provisional] (the
    // TT-1218 gate), and phraseIneligible (never eligible). Named nonLocal, NOT server:
    // ineligible phrases count under ineligiblePhrases, not serverPhrases (see
    // recordLocalAsrServerPhrase), so a "server" name would label a zero-serverPhrases row
    // with a server reason. This is the flat breakdown that otherwise needed unnesting the
    // lifecycle blob's events[] (TT-1174). Snapshotted here because the failure path
    // disposes below, which clears localAsrReportedFallbacks.
    // SORTED so the array is order-independent: Athena groups arrays by value INCLUDING
    // order, so an unsorted set would split ['runtimeNotReady','handlerDeclined'] from its
    // reverse into different GROUP BY buckets — the exact breakdown this field exists for.
    nonLocalPhraseReasons: [...localAsrReportedFallbacks].sort(),
    route: localAsrPlacementRoute,
    ready: Boolean(localAsr && localAsr.isReady),
    localPhrases: localAsrPhraseCounts.local,
    serverPhrases: localAsrPhraseCounts.server,
    ineligiblePhrases: localAsrPhraseCounts.ineligible,
  } : null;
  // Snapshotted with the detail, not read at emission time: the activityFailed teardown
  // below runs disposeLocalPlacementAsr, which resets the warmup routing before the
  // finally block emits the summary.
  const activityEndedWarmupDecision = touched ? getLocalAsrWarmupRouteDecision() : null;

  // Teardown runs BEFORE the flush, so the final phrase's decode.phraseStats lands in
  // this activity's object rather than the next
  // one's — but it is fenced in try/finally so a throw out of teardown cannot cost the
  // activity its S3 object, nor leave the counts unreset and inflate the next activity's
  // tallies. The throw surface is small (handler callbacks are individually try/caught),
  // which is exactly why making the flush unconditional is worth its two lines.
  //
  // A phrase still decoding at this point reports from its own decode loop, which can land
  // after the flush and ride into the next checkpoint's object. On the activityFailed path
  // it ALWAYS does: disposeLocalPlacementAsr is not awaited and dispose() settles pending
  // work before finalizing, so its `tornDown` stats are at minimum a microtask behind this
  // flush. That is the accepted cost of keeping this path synchronous — awaiting teardown
  // is what dropped the next phrase's opening audio. The runtime.activityEnded marker still
  // carries the correct activityId, so attribution by trailing marker is unaffected.
  try {
    if (localAsrActivityFailed) {
      disposeLocalPlacementAsr();
      // Retire, don't re-boot: see localAsrRetiredAfterActivityFailure. Set AFTER the
      // dispose call, whose synchronous prefix is what resets the rest of the runtime state.
      localAsrRetiredAfterActivityFailure = true;
    } else {
      localAsrPlacementRoute = LOCAL_ASR_PLACEMENT_ROUTE.SERVER;
      if (localAsr) {
        localAsr.endPhrase();
      }
    }
  } finally {
    // Reset the live tallies FIRST. Everything below reads the activityEndedDetail snapshot
    // (and its already-sorted nonLocalPhraseReasons), never these, so resetting up front means
    // a throw out of the emit path — logSessionInfo stringifies synchronously before its
    // first await — cannot skip the reset and inflate the next activity's counts, which is
    // exactly what this try/finally exists to prevent.
    localAsrPhraseCounts = { local: 0, server: 0, ineligible: 0 };
    localAsrReportedFallbacks = new Set();
    if (touched) {
      // These counts are the answer to "did this activity actually use local ASR", which
      // placement_manager's eligibility-based `localAsr` cannot give: an activity whose
      // runtime never came up reports localAsr there and localPhrases: 0 here.
      // ineligiblePhrases separates "never supposed to decode locally" (a Spanish story, an
      // assessment activity) from serverPhrases' "could have, did not".
      recordLocalAsrLifecycle('runtime.activityEnded', activityEndedDetail);
      // Keyed to the snapshot above, not to whatever getUserData() reads by the time the
      // upload resolves the key: this checkpoint runs inside session teardown.
      flushLocalAsrLifecycle('activityEnded', { activityId: activityEndedDetail.activityId });
      // The same answer as runtime.activityEnded, but as flat fields (TT-1174): the
      // lifecycle object nests every event inside events[], so "how many activities
      // fell back to the server, by reason" meant unnesting JSON in Athena. warmupRoute
      // and warmupVerdict ride along so that query does not need a join against
      // placement_manager to separate a warmup-gated session from a mid-activity latch.
      // Note the pair is PER-RUNTIME on a per-activity record: warmup measures once and
      // re-measures only after a dispose (activity failure), so a healthy multi-activity
      // session repeats activity 1's verdict on every row. Fleet queries counting
      // verdicts must dedupe by session first or they get activities-weighted numbers.
      // Gate on CONTENT, not on a first-seen activityId. This runs on every
      // endUniversalSocketSession, including the mid-activity blob-error recovery
      // (see the fr.onerror path), which tears the socket down and restarts listening
      // WITHOUT ending the activity. A first-seen latch would keep that early, partial,
      // healthy-looking row and suppress the real end-of-activity row — hiding an
      // activityFailureReason that latches after the restart. Emitting only when there is
      // something to report instead drops the all-zero repeat-teardown rows (counts are
      // reset after each emit) while still emitting the real end.
      //
      // FLEET QUERY CONTRACT: a mid-activity recovery emits MORE THAN ONE row per
      // activityId, and because the counts reset after each emit those rows hold DISJOINT
      // partitions of the activity. Aggregate PER FIELD, not last-row-wins:
      //   - counts (localPhrases/serverPhrases/ineligiblePhrases): SUM across rows
      //     (last-wins undercounts on exactly the blob-error sessions this is hunting).
      //   - activityFailed: bool_or (a failure that latched before a mid-activity teardown
      //     is cleared by disposeLocalPlacementAsr, so a later row can read false).
      //   - activityFailureReason: latest non-null.
      //   - nonLocalPhraseReasons: array_union (each row holds only its partition's reasons);
      //     each row's array is sorted, so equality/GROUP BY on it is order-independent.
      //   - ready: bool_or. It is a pre-teardown snapshot, so a failed activity's first row
      //     reads true and its post-dispose row reads false — `WHERE ready = false` (the
      //     "runtime never came up" query) would overcount without bool_or.
      //   - route: last-phrase placement, ambiguous across rows (local then server); prefer
      //     serverPhrases/nonLocalPhraseReasons, which say more than a last-phrase route.
      // Order rows by the `timestamp` field below, not by parsing Date.now() out of the
      // S3 object name. NOTE this table is not an activity census — the content gate drops
      // touched-but-empty activities — so a fell-back RATE must take its denominator from
      // placement_manager, not count(*) here.
      const hasSomethingToReport = activityEndedDetail.activityFailed
        || activityEndedDetail.localPhrases > 0
        || activityEndedDetail.serverPhrases > 0
        || activityEndedDetail.ineligiblePhrases > 0
        // Self-defending: every localAsrReportedFallbacks.add is paired with a count
        // increment today, so this is redundant now — but it keeps a future reason recorded
        // without a tally from being silently dropped from the flat record.
        || activityEndedDetail.nonLocalPhraseReasons.length > 0;
      if (hasSomethingToReport) {
        logSessionInfo('local_asr_activity_summary', {
          ...activityEndedDetail,
          // Orders the multiple rows one activityId can produce (see the contract above),
          // matching the timestamp field the other flat log types here carry.
          timestamp: Date.now(),
          // warmupRoute: one of LOCAL_ASR_ROUTE. A touched runtime whose warmup has not
          // settled is UNDECIDED — the same spelling placement_manager uses for that state,
          // not a third `null` spelling (LOCAL_ASR_ROUTE.UNDECIDED exists so this field's
          // producer and any fleet query filtering it agree on one word for each state).
          warmupRoute: activityEndedWarmupDecision?.route ?? LOCAL_ASR_ROUTE.UNDECIDED,
          // warmupVerdict: one of WARMUP_VERDICT, or null when no decision has settled.
          warmupVerdict: activityEndedWarmupDecision?.verdict ?? null,
        }, activityEndedDetail.activityId || null);
      }
    }
  }
}

/**
 * A live read of the local ASR runtime's identity, for asr/localAsrWarmup.
 *
 * Private on purpose: asr/localAsrWarmup is its only production consumer, so nothing
 * outside this file needs to reach the runtime directly. (One test-only passthrough is
 * exported below — the generation-bump ordering it observes cannot be checked from the
 * warmup side, which is precisely why that ordering needs pinning here.) It is a
 * function rather than two values because the warmup module's staleness guards compare a
 * pin taken at run start against what is current when the run settles ~6.5s later.
 * @returns {{handler: object|null, lifecycleId: number}}
 */
function getLocalAsrRuntime() {
  return { handler: localAsr, lifecycleId: localAsrLifecycleId };
}

/**
 * Test-only view of getLocalAsrRuntime. The accessor itself stays private because
 * asr/localAsrWarmup is its only production consumer; this exists so the TT-1231 wiring
 * tests can observe the generation-bump ordering that the warmup module's staleness
 * guards depend on and cannot check for themselves.
 * @returns {{handler: object|null, lifecycleId: number}}
 */
export function getLocalAsrRuntimeForTests() {
  return getLocalAsrRuntime();
}

/**
 * @see asr/localAsrWarmup.warmLocalAsrForStory — this wrapper exists only to supply the
 * runtime accessor, which lives here and which a caller has no way to build.
 *
 * Not a general front door for the warmup module: its sibling isLocalAsrWarmupSpent
 * needs no runtime and is imported straight from asr/localAsrWarmup by ReaderManager.
 * The rule is that a warmup function is wrapped here IF AND ONLY IF it needs the
 * runtime — anything else would grow this file back for no reason (TT-1231).
 * @param {object} story
 * @returns {Promise<void>}
 */
export async function warmLocalAsrForStory(story) {
  return localAsrWarmup.warmLocalAsrForStory(story, { getRuntime: getLocalAsrRuntime });
}

/**
 * Disposes the local ASR runtime for session teardown.
 * @returns {Promise<void>}
 */
export function disposeLocalPlacementAsr() {
  // Not itself `async`: the body below must keep running SYNCHRONOUSLY up to its first
  // await (callers rely on the state resets landing before they read them — see
  // resetLocalAsrForNextActivity), and the wrapper only records the in-flight promise
  // for ensureLocalPlacementAsrBooted.
  const run = runDisposeLocalPlacementAsr();
  localAsrDisposePromise = run;
  run.finally(() => {
    if (localAsrDisposePromise === run) {
      localAsrDisposePromise = null;
    }
  }).catch(() => {});
  return run;
}

/** The teardown itself; only disposeLocalPlacementAsr calls this. Never rejects: the
 * whole body is guarded, not just handler.dispose(), because the returned promise is
 * awaited by ensureLocalPlacementAsrBooted and dropped un-awaited by three App paths. */
async function runDisposeLocalPlacementAsr() {
  try {
    await runDisposeLocalPlacementAsrBody();
  } catch (error) {
    recordLocalAsrFailure('runtime.disposeFailed', { error: error?.message });
  }
}

async function runDisposeLocalPlacementAsrBody() {
  localAsrLifecycleId += 1;
  localAsrInitializing = false;
  // Move the in-flight boot's handle off localAsrInitPromise: it is doomed now (the
  // lifecycle bump makes it resolve false and dispose its own handler when createModel
  // settles — possibly minutes out on a slow link). Left on localAsrInitPromise, the next
  // ensureLocalPlacementAsrBooted would join it and hand the next student server ASR for
  // nothing. But do NOT simply discard it: createModel cannot be interrupted, so the doomed
  // worker keeps running, and a fresh boot starting beside it would put two WASM engines on
  // the device at once (the CR-10679 memory event, PR #6006 review #1). Park it as "settling"
  // so the next boot waits for it to fully tear down, then starts fresh (warm if its download
  // completed in the meantime).
  if (localAsrInitPromise) {
    const settling = localAsrInitPromise;
    localAsrSettlingInitPromise = settling;
    settling.finally(() => {
      if (localAsrSettlingInitPromise === settling) {
        localAsrSettlingInitPromise = null;
      }
    }).catch(() => {});
  }
  localAsrInitPromise = null;
  // The next runtime is a cold one, so it earns a fresh capability measurement — and a
  // fresh routing decision to go with it. resetLocalAsrWarmup keeps those two inseparable;
  // see its docblock in asr/localAsrWarmup for why that matters.
  localAsrWarmup.resetLocalAsrWarmup();
  localAsrPlacementRoute = LOCAL_ASR_PLACEMENT_ROUTE.SERVER;
  localAsrActivityFailed = false;
  localAsrActivityFallbackReason = null;
  localAsrPhraseCounts = { local: 0, server: 0, ineligible: 0 };
  localAsrReportedFallbacks = new Set();

  const handler = localAsr;
  if (!handler) {
    return;
  }

  try {
    await handler.dispose();
  } catch (error) {
    recordLocalAsrFailure('runtime.disposeFailed', { error: error?.message });
  }

  if (localAsr === handler) {
    localAsr = null;
  }
}
